<template>
  <div id="group-dashboard">
    <v-container fluid v-if="groupLoaded" class="dashboard-container">
      <div class="main-content">
        <!-- Group Header -->
        <v-card class="mb-4" outlined>
          <v-row align="center" class="pa-4">
            <v-col
              cols="auto"
              v-if="group.mainLogo && !failedLogos.has('main')"
            >
              <v-img
                :src="getLogoUrl(group.mainLogo)"
                max-width="200"
                max-height="150"
                contain
                @error="
                  () => {
                    failedLogos.add('main');
                    $forceUpdate();
                  }
                "
              >
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-2"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-col>
            <v-col cols="auto" v-else-if="group.mainLogo">
              <v-icon size="120" color="primary">mdi-image-off</v-icon>
            </v-col>
            <v-col>
              <v-card-title class="text-h4" style="word-break: break-word">{{
                group.groupName
              }}</v-card-title>
              <v-card-text>{{ group.textDescription }}</v-card-text>
              <v-btn
                v-if="group.linkToOrg"
                :href="group.linkToOrg"
                target="_blank"
                text
                color="primary"
              >
                <v-icon left>mdi-open-in-new</v-icon>
                {{ t("Visit Website") }}
              </v-btn>
            </v-col>
            <v-col cols="auto" class="d-flex align-start">
              <v-select
                :items="languages"
                v-model="selectedLanguage"
                @change="setLanguage(selectedLanguage)"
                style="max-width: 80px"
                dense
                hide-details
                :menu-props="{ zIndex: 1000 }"
              ></v-select>
            </v-col>
          </v-row>
        </v-card>

        <!-- Summary Statistics -->
        <v-card class="mb-4" outlined>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="3">
                <v-card color="primary" dark>
                  <v-card-text class="text-center">
                    <div class="text-h3 text-no-wrap">{{ activeStations }}</div>
                    <div>{{ t("Stations") }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card color="success" dark>
                  <v-card-text class="text-center">
                    <div
                      :class="totalDetections > 9999 ? 'text-h4' : 'text-h3'"
                      class="text-no-wrap"
                    >
                      {{ formatDetectionCount(totalDetections) }}
                    </div>
                    <div>{{ t("Detections Yesterday") }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card color="info" dark>
                  <v-card-text>
                    <div class="text-h6">{{ t("Top Species Yesterday") }}</div>
                    <v-chip
                      v-for="species in topSpecies"
                      :key="species.spCode"
                      class="ma-1 text-no-wrap"
                      color="white"
                      text-color="info"
                    >
                      {{ t(species.spCode, species.cn) }}: {{ species.count }}
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Map -->
        <v-card class="mb-4 map-card" outlined>
          <l-map
            :zoom="mapZoom"
            :center="mapCenter"
            :options="mapOptions"
            :bounds="mapBounds"
            style="height: 500px; width: 100%"
            @update:zoom="zoomUpdate"
            @update:center="centerUpdate"
          >
            <l-tile-layer
              :key="mapStyle"
              :url="currentBaseLayer.url"
              :attribution="currentBaseLayer.attribution"
            />
            <v-marker-cluster
              :options="{ chunkedLoading: true, maxClusterRadius: 60 }"
            >
              <l-marker
                v-for="haikubox in haikuboxLocations"
                :key="haikubox.serial"
                :lat-lng="[haikubox.lat, haikubox.lon]"
                :icon="haikubox.hasRecentActivity ? activeIcon : inactiveIcon"
                @click="selectHaikubox(haikubox)"
              >
                <l-tooltip>
                  {{ haikubox.haikuboxName || haikubox.name }}
                  <br />
                  <span v-if="haikubox.hasRecentActivity" style="color: #4caf50"
                    >● {{ t("Active (24h)") }}</span
                  >
                  <span v-else style="color: #9e9e9e">● {{ t("Inactive") }}</span>
                </l-tooltip>
              </l-marker>
            </v-marker-cluster>
          </l-map>
          <v-btn
            small
            depressed
            class="map-style-toggle"
            @click="mapStyle = mapStyle === 'topo' ? 'satellite' : 'topo'"
          >
            <v-icon left small>{{
              mapStyle === "topo" ? "mdi-satellite-variant" : "mdi-map"
            }}</v-icon>
            {{ mapStyle === "topo" ? t("Satellite") : t("Topo Map") }}
          </v-btn>
        </v-card>

        <!-- Selected Haikubox Detections -->
        <v-card
          v-if="selectedHaikubox"
          outlined
          class="mb-4"
          style="overflow: visible"
        >
          <v-card-title
            class="d-flex align-center flex-wrap"
            style="word-break: break-word"
          >
            <span class="mr-2">{{
              selectedHaikubox.haikuboxName || selectedHaikubox.name
            }}</span>
            <v-spacer></v-spacer>
            <v-menu
              v-if="allowDatePicker"
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn text v-bind="attrs" v-on="on">
                  <v-icon left>mdi-calendar</v-icon>
                  {{ selectedDate === boxToday ? t("Today") : selectedDate }}
                </v-btn>
              </template>
              <v-date-picker
                v-model="selectedDate"
                :min="minDate"
                :max="boxToday"
                no-title
                @input="onDateChange"
              ></v-date-picker>
            </v-menu>
            <span v-else class="text-button mx-2">{{ t("Today") }}</span>

            <!-- CSV download, as on the Haikubox ALL page -->
            <v-dialog v-if="allowCsvDownload" v-model="csvDialog" max-width="600px">
              <template v-slot:activator="{ on, attrs }">
                <v-btn text v-bind="attrs" v-on="on" :loading="csvLoading">
                  <v-icon left>mdi-file-download-outline</v-icon>
                  CSV
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="text-h5">{{ t("Download CSV") }}</span>
                </v-card-title>
                <v-card-text>
                  <v-checkbox
                    dense
                    v-model="csvSummary"
                    :label="t('Download Summary')"
                  ></v-checkbox>
                  <v-checkbox
                    dense
                    v-model="csvDailyCounts"
                    :label="t('Download Daily Counts')"
                  ></v-checkbox>
                  <v-checkbox
                    dense
                    v-model="csvRaw"
                    :label="t('DownloadInfo')"
                  ></v-checkbox>
                  <v-date-picker
                    v-if="csvRaw"
                    v-model="csvDates"
                    range
                    no-title
                    :min="minDate"
                    :max="boxToday"
                  ></v-date-picker>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="csvDialog = false">
                    {{ t("Close") }}
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    :disabled="
                      !(csvSummary || csvDailyCounts || csvRaw) ||
                      (csvRaw && csvDates.length === 0)
                    "
                    @click="downloadCSV()"
                  >
                    {{ t("Download CSV") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- Only levels at or above the group's minimum; nothing to pick at High -->
            <v-btn-toggle
              v-if="levelOptions.length > 1"
              v-model="levelFilter"
              mandatory
              dense
              class="mx-2"
            >
              <v-btn
                v-for="option in levelOptions"
                :key="option.value"
                small
                :value="option.value"
                >{{ t(option.label) }}</v-btn
              >
            </v-btn-toggle>
            <v-chip v-else small class="mx-2">{{ t("High") }}</v-chip>
            <v-btn icon @click="selectedHaikubox = null">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-alert v-if="detectionsError" type="warning" dense text class="mx-4">
            {{ detectionsError }}
          </v-alert>

          <v-container style="overflow: visible">
            <div v-if="loadingDetections" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div
              v-else-if="groupedDetections.length === 0"
              class="text-center pa-8 grey--text"
            >
              {{ t("No detections for this day") }}
            </div>
            <v-row v-else dense style="overflow: visible">
              <v-col
                cols="12"
                sm="6"
                md="6"
                lg="4"
                xl="3"
                v-for="speciesGroup in groupedDetections"
                :key="speciesGroup.spCode"
              >
                <v-card
                  dark
                  color="light-blue darken-4"
                  class="species-card mx-auto"
                  style="word-break: break-word"
                >
                  <v-card-title>{{
                    t(speciesGroup.spCode, speciesGroup.cn)
                  }}</v-card-title>
                  <v-card-subtitle class="font-italic">{{
                    speciesGroup.sn
                  }}</v-card-subtitle>

                  <v-card-subtitle class="py-0">
                    <!-- Image -->
                    <img
                      v-if="
                        speciesGroup.window === 'image' &&
                        !hidePics &&
                        !failedImages.has(speciesGroup.spCode)
                      "
                      width="100%"
                      :src="getImageUrl(speciesGroup.spCode)"
                      :onerror="`this.onerror=null;this.src='https://haikubox-images.s3.amazonaws.com/placeholder.jpeg';`"
                    />
                    <div
                      v-else-if="speciesGroup.window === 'image'"
                      style="
                        height: 240px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background-color: #f5f5f5;
                      "
                    >
                      <v-icon size="80" color="grey">mdi-bird</v-icon>
                    </div>

                    <!-- Chart loading -->
                    <div
                      v-if="speciesGroup.loadingChart"
                      class="text-center pa-4"
                      style="height: 240px"
                    >
                      <v-progress-circular
                        indeterminate
                        color="blue"
                      ></v-progress-circular>
                    </div>

                    <!-- 24 hour donut chart -->
                    <div
                      v-else-if="
                        speciesGroup.window === 'daily' &&
                        speciesGroup.hourlyCountPlotData
                      "
                    >
                      <fusioncharts
                        type="doughnut2d"
                        width="100%"
                        height="240"
                        dataFormat="json"
                        :dataSource="speciesGroup.hourlyCountPlotData"
                      >
                      </fusioncharts>
                    </div>

                    <!-- 14 day heat map -->
                    <div
                      v-else-if="
                        speciesGroup.window === 'heatmap' && speciesGroup.heatmap
                      "
                    >
                      <Plotly
                        :data="speciesGroup.heatmap"
                        :display-mode-bar="false"
                        :layout="plotlyLayoutHeatmap"
                      ></Plotly>
                    </div>

                    <!-- Recent history: daily counts, past 3 months -->
                    <div
                      v-else-if="
                        speciesGroup.window === 'monthly' &&
                        speciesGroup.dailyCountPlotData
                      "
                    >
                      <v-card-text class="ma-3"
                        ><v-icon>mdi-music-note</v-icon>
                        {{ t("Past 3 Months") }}
                      </v-card-text>
                      <column-chart
                        :colors="['#90A4AE']"
                        :height="300"
                        :library="{
                          scales: {
                            xAxes: [{ ticks: { fontColor: '#ffffff' } }],
                            yAxes: [{ ticks: { fontColor: '#ffffff' } }],
                          },
                        }"
                        :data="speciesGroup.dailyCountPlotData"
                      ></column-chart>
                    </div>

                    <!-- Spectrogram -->
                    <div
                      v-if="speciesGroup.window === 'spectrogram'"
                      style="width: 100%; position: relative"
                    >
                      <div
                        v-if="speciesGroup.loadingSpectrogram"
                        class="text-center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="blue"
                        ></v-progress-circular>
                        <div class="mt-2">Generating spectrogram...</div>
                      </div>

                      <div
                        v-else-if="speciesGroup.spectrogram"
                        style="position: relative; width: 100%; margin-top: 18px"
                      >
                      <!-- Frequency label above spectrogram (as in Haikubox.vue) -->
                      <div
                        v-if="speciesGroup.maxFreqKHz"
                        style="position: absolute; left: 2px; top: -18px; z-index: 10"
                        class="text-caption grey--text"
                      >
                        {{ speciesGroup.maxFreqKHz }} kHz
                      </div>
                      <!-- Fixed size container -->
                      <div style="position: relative; width: 100%">
                        <Plotly
                          ref="spectrogram"
                          :data="speciesGroup.spectrogram"
                          :layout="
                            speciesGroup.spectrogramLayout || plotlyLayout
                          "
                          :display-mode-bar="false"
                          :config="{
                            responsive: false, // Try false for iOS
                            displaylogo: false,
                            staticPlot: true, // This can help performance
                          }"
                          style="width: 100%"
                        ></Plotly>

                        <!-- Progress bar overlay -->
                        <div
                          v-if="
                            speciesGroup.isPlaying &&
                            speciesGroup.playbackProgress > 0
                          "
                          class="spectrogram-progress-bar"
                          :style="{ left: speciesGroup.playbackProgress + '%' }"
                        ></div>
                      </div>
                      <!-- Time of the visible window; follows the scroll -->
                      <v-row no-gutters>
                        <v-col cols="2" class="text-caption">
                          {{ (speciesGroup.currentWindowStart || 0).toFixed(1) }}s
                        </v-col>
                        <v-col cols="8" class="text-center">
                          <span
                            v-if="speciesGroup.isPlaying"
                            class="yellow--text text-caption"
                            >▶ Playing...</span
                          >
                        </v-col>
                        <v-col cols="2" class="d-flex justify-end text-caption">
                          {{
                            (
                              (speciesGroup.currentWindowStart || 0) +
                              (speciesGroup.windowDuration || 3)
                            ).toFixed(1)
                          }}s
                        </v-col>
                      </v-row>
                      </div>
                      <div v-else class="text-center pa-4">
                        <v-icon size="64" color="grey">mdi-chart-bubble</v-icon>
                        <div class="mt-2 grey--text">
                          No spectrogram available
                        </div>
                      </div>
                    </div>
                  </v-card-subtitle>

                  <!-- About / eBird -->
                  <v-row class="my-0 py-0">
                    <v-card-text class="my-0 py-0">
                      <v-btn
                        text
                        v-if="isBat(speciesGroup)"
                        href="https://www.batcon.org/about-bats/bats-101/"
                        target="_blank"
                        color="light-blue"
                        >BCI</v-btn
                      >
                      <v-btn
                        text
                        v-if="
                          !isBat(speciesGroup) &&
                          selectedHaikubox &&
                          selectedHaikubox.lon < -30
                        "
                        :href="
                          'https://www.allaboutbirds.org/guide/' +
                          speciesGroup.cn.replace(/ /g, '_')
                        "
                        target="_blank"
                        color="light-blue"
                        >{{ t("About") }}</v-btn
                      >
                      <v-btn
                        text
                        v-if="!isBat(speciesGroup)"
                        :href="'https://ebird.org/species/' + speciesGroup.spCode"
                        target="_blank"
                        color="light-blue"
                        >eBird</v-btn
                      >
                    </v-card-text>
                  </v-row>

                  <!-- Card navigation -->
                  <v-row class="mb-1" no-gutters>
                    <v-col cols="12">
                      <v-avatar
                        class="ml-4"
                        size="24"
                        style="cursor: pointer"
                        v-tooltip.bottom-start="t('Image')"
                        @click="setWindowRecent(speciesGroup, 'image')"
                      >
                        <v-img
                          v-if="!failedImages.has(speciesGroup.spCode)"
                          :src="getImageUrl(speciesGroup.spCode)"
                          @error="() => onImageError(speciesGroup.spCode)"
                        ></v-img>
                        <v-icon v-else size="18">mdi-bird</v-icon>
                      </v-avatar>
                      <v-btn
                        icon
                        class="ml-0"
                        v-tooltip.bottom-start="t('Hourly Chart')"
                        @click="setWindowRecent(speciesGroup, 'daily')"
                      >
                        <v-icon alt="hourly chart">mdi-chart-donut</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="ml-0"
                        v-tooltip.bottom-start="t('Daily Chart')"
                        @click="setWindowRecent(speciesGroup, 'monthly')"
                      >
                        <v-icon alt="daily chart">mdi-chart-bar</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="ml-0"
                        v-tooltip.bottom-start="t('Heat Map')"
                        @click="setWindowRecent(speciesGroup, 'heatmap')"
                      >
                        <v-icon alt="heat map">mdi-checkerboard</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        class="ml-0"
                        v-tooltip.bottom-start="t('Spectrogram')"
                        @click="setWindowRecent(speciesGroup, 'spectrogram')"
                      >
                        <v-icon alt="spectrogram">mdi-chart-bubble</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Play -->
                  <v-card-actions
                    class="mx-0 my-0 py-0"
                    v-if="
                      speciesGroup.currentDetection &&
                      speciesGroup.currentDetection.hasAudio
                    "
                  >
                    <v-row>
                      <v-col cols="12">
                        <v-btn
                          class="mx-1 my-1"
                          @click="
                            speciesGroup.isPlaying && !speciesGroup.processingEnhanced
                              ? stopCurrentAudio()
                              : playSound(speciesGroup.currentDetection)
                          "
                          :color="
                            speciesGroup.isPlaying && !speciesGroup.processingEnhanced
                              ? 'orange'
                              : 'light-blue darken-1'
                          "
                          :disabled="speciesGroup.processingEnhanced"
                        >
                          <v-icon left>{{
                            speciesGroup.isPlaying && !speciesGroup.processingEnhanced
                              ? "mdi-stop"
                              : "mdi-play-circle"
                          }}</v-icon>
                          <v-divider class="mx-1" vertical></v-divider>
                          {{ t("Play") }}
                        </v-btn>
                        <v-btn
                          class="mx-1"
                          @click="
                            playEnhanced(speciesGroup.currentDetection, speciesGroup)
                          "
                          color="light-blue darken-1"
                          :disabled="
                            speciesGroup.processingEnhanced || speciesGroup.isPlaying
                          "
                        >
                          <v-progress-circular
                            v-if="speciesGroup.processingEnhanced"
                            :size="16"
                            :width="2"
                            indeterminate
                            color="white"
                            class="mr-2"
                          ></v-progress-circular>
                          <v-icon v-else left>mdi-play-circle</v-icon>
                          <v-divider class="mx-1" vertical></v-divider>
                          {{ speciesGroup.cn === "Bat" ? t("Play Slow") : t("Enhance") }}
                        </v-btn>

                        <!-- Clip length -->
                        <v-chip
                          v-if="
                            maxClipSeconds > 3 &&
                            isLongClipFile(speciesGroup.currentDetection)
                          "
                          class="mx-2"
                          small
                          color="light-blue darken-1"
                        >
                          <v-btn
                            icon
                            x-small
                            @click="updateClipSeconds(-3)"
                            :disabled="clipSeconds <= 3"
                            class="mr-1"
                          >
                            <v-icon small>mdi-minus</v-icon>
                          </v-btn>
                          {{ clipSeconds }}s
                          <v-btn
                            icon
                            x-small
                            @click="updateClipSeconds(3)"
                            :disabled="clipSeconds >= maxClipSeconds"
                            class="ml-1"
                          >
                            <v-icon small>mdi-plus</v-icon>
                          </v-btn>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-card-actions>

                  <!-- Detection selector -->
                  <v-card-actions class="mx-0 my-0 py-0">
                    <v-row>
                      <v-col cols="12">
                        <v-select
                          class="mx-2 my-0 py-0"
                          :items="speciesGroup.detections"
                          v-model="speciesGroup.currentDetection"
                          :item-text="(item) => detectionListName(item)"
                          item-value="id"
                          @change="handleDetectionChange(speciesGroup, $event)"
                          return-object
                          single-line
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-card-actions>

                  <!-- Download -->
                  <v-card-actions
                    class="mx-0 my-0 pt-0"
                    v-if="
                      allowAudioDownload &&
                      speciesGroup.currentDetection &&
                      speciesGroup.currentDetection.hasAudio
                    "
                  >
                    <v-row align="center" justify="center" no-gutters>
                      <v-col cols="12">
                        <v-btn
                          class="mx-2 my-0"
                          color="light-blue darken-1"
                          fab
                          small
                          dark
                          v-tooltip.bottom-start="t('Download')"
                          @click="downloadAudio(speciesGroup.currentDetection)"
                          ><v-icon alt="download">mdi-download</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </div>

      <!-- Spacer to push partners to bottom -->
      <div class="flex-spacer"></div>

      <!-- Partner Logos - Always at bottom -->
      <v-card
        v-if="group.logoList && group.logoList.length > 0"
        outlined
        class="partners-card"
      >
        <v-card-title>{{ t("Partners") }}</v-card-title>
        <v-card-text>
          <v-row align="center" justify="center">
            <v-col
              v-for="(logo, index) in group.logoList"
              :key="index"
              cols="auto"
            >
              <div class="text-center">
                <div class="partner-logo-box mb-2">
                  <img
                    v-if="logo && !failedLogos.has(`partner-${index}`)"
                    :src="getLogoUrl(logo)"
                    :alt="group.logoName[index] || 'Partner logo'"
                    class="partner-logo"
                    @error="
                      () => {
                        failedLogos.add(`partner-${index}`);
                        $forceUpdate();
                      }
                    "
                  />
                  <v-icon v-else-if="logo" size="80" color="grey"
                    >mdi-image-off</v-icon
                  >
                </div>
                <div v-if="group.logoName[index]">
                  {{ group.logoName[index] }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Loading State -->
    <v-container v-else>
      <v-row justify="center" align="center" style="height: 500px">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-row>
    </v-container>

    <!-- Access-key prompt for groups that require a key -->
    <v-dialog v-model="keyRequired" max-width="440" persistent>
      <v-card>
        <v-card-title>{{ t("Access key required") }}</v-card-title>
        <v-card-text>
          <p>
            {{
              t(
                "This group's detections are private. Enter the access key from the group's owner."
              )
            }}
          </p>
          <v-text-field
            v-model="keyInput"
            :label="t('Access key')"
            :error-messages="keyError"
            autofocus
            @keyup.enter="submitKey"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!keyInput.trim()" @click="submitKey">{{
            t("Open")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { API } from "aws-amplify";
import { latLng, latLngBounds } from "leaflet";
import L from "leaflet";
import { LMap, LTileLayer, LMarker, LTooltip } from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Plotly } from "vue-plotly";
import moment from "moment-timezone";
import { groupBySlug } from "../graphql/queries";
import {
  getSummary,
  getDetections,
  getAudio,
  getDailyCounts,
  getStoredKey,
  storeKey,
  KeyRequiredError,
} from "../groupsApi";
import { FFT } from "dsp.js";
import { AudioContext } from "standardized-audio-context";
import { unmute } from "../unmute.min.js";

// Global audio context (from Notifications.vue pattern)
let globalAudioContext = null;
let globalGainNode = null;

// Sound categories that aren't species; hidden from the cards, as in Haikubox.vue.
const NON_SPECIES = new Set([
  "",
  "soundscape",
  "nocode",
  "Noise",
  "Gun",
  "Power tools",
  "Engine",
  "Siren",
  "Fireworks",
  "Environmental",
  "Human vocal",
  "Human whistle",
  "Human non-vocal",
  "Dog",
]);

// FFT imports for enhanced playback
var fft = require("fft-js").fft,
  ifft = require("fft-js").ifft;

export default {
  name: "GroupDashboard",
  components: {
    Plotly,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    "v-marker-cluster": Vue2LeafletMarkerCluster,
  },
  data() {
    return {
      // i18n
      currentTranslations: {},
      cnToSpCode: {},
      selectedLanguage: "en",
      languages: ["da", "de", "en", "es", "fi", "fr", "it", "nl", "no", "pt", "sv"],

      group: null,
      groupLoaded: false,
      haikuboxLocations: [],
      selectedHaikubox: null,
      selectedDetections: [],
      activeStations: 0,
      totalDetections: 0,
      topSpecies: [],
      hidePics: false,
      failedLogos: new Set(),
      failedImages: new Set(),

      // Groups API
      summary: null, // GET /groups/{slug}/summary
      selectedDate: null, // YYYY-MM-DD, the selected box's local day
      dateMenu: false,
      levelFilter: "all", // "all" | "med" | "high"
      detectionsError: "",
      loadingDetections: false,
      detectionsRequest: 0, // drops responses from superseded loads
      // Past days' detections for the heat map, keyed "serial|date". Past days
      // don't change, so they're fetched once. (A Map, so Vue doesn't observe it.)
      dayDetectionsCache: new Map(),
      // daily-counts responses, keyed "serial|cn"
      dailyCountsCache: new Map(),
      // CSV download dialog
      csvDialog: false,
      csvSummary: true,
      csvDailyCounts: false,
      csvRaw: false,
      csvDates: [],
      csvLoading: false,
      keyRequired: false, // shows the access-key prompt
      keyInput: "",
      keyError: "",

      // Audio processing
      audioContexts: new Map(), // Store multiple contexts for different sample rates
      currentAudioContext: null,
      fftLength: 512,
      halfLength: 256,
      playbackGain: 1,

      // Audio cache to store fetched audio buffers
      audioCache: new Map(),

      // Enhanced audio cache
      lastEnhancedId: "",
      lastEnhancedAudio: null,
      lastEnhancedClipSeconds: 12,

      // Audio playback tracking
      currentAudioSource: null,
      playbackStartTime: null,
      playbackDuration: 0,
      playbackProgress: 0,
      playbackInterval: null,

      // Clip duration settings
      clipSeconds: 3,
      maxClipSeconds: 3, // raised to 12 once a clip longer than 3s plays

      // Dropdown control
      expandedSpecies: null,

      // Map settings
      mapZoom: 5,
      mapCenter: latLng(39, -94),
      mapBounds: null,
      mapOptions: {
        zoomSnap: 0.5,
        maxZoom: 18,
        minZoom: 2,
        scrollWheelZoom: false,
      },
      mapStyle: "topo", // 'topo' | 'satellite'
      baseLayers: {
        topo: {
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
          attribution:
            "Tiles &copy; Esri &mdash; Esri, TomTom, Garmin, FAO, NOAA, USGS, &copy; OpenStreetMap contributors, and the GIS User Community",
        },
        satellite: {
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
        },
      },

      // Marker icons
      activeIcon: null,
      inactiveIcon: null,

      // Heat map settings (as in Haikubox.vue)
      plotlyLayoutHeatmap: {
        xaxis: {
          autorange: true,
          showgrid: false,
          zeroline: false,
          showline: false,
          autotick: true,
          ticks: "",
          showticklabels: true,
          fixedrange: true,
        },
        yaxis: {
          autorange: true,
          showgrid: false,
          zeroline: false,
          showline: false,
          autotick: true,
          ticks: "",
          showticklabels: true,
          fixedrange: true,
        },
        title: "Calls Per Hour",
        margin: { l: 60, r: 40, b: 60, t: 40, pad: 4 },
        height: 300,
        hovermode: false,
      },

      // Spectrogram settings
      plotlyLayout: {
        xaxis: {
          autorange: true,
          showgrid: false,
          zeroline: false,
          showline: false,
          showticklabels: false,
          fixedrange: true,
        },
        yaxis: {
          autorange: true,
          showgrid: false,
          zeroline: false,
          showline: false,
          showticklabels: false,
          fixedrange: true,
        },
        margin: { l: 0, r: 0, b: 0, t: 0, pad: 4 },
        height: 200,
        hovermode: false,
      },
    };
  },
  computed: {
    currentBaseLayer() {
      return this.baseLayers[this.mapStyle];
    },
    // Viewer features the group's owner can turn off. Unset means on, so
    // groups saved before these settings keep everything. These only hide
    // the controls; they aren't enforced by the API.
    allowDatePicker() {
      return !this.group || this.group.allowDatePicker !== false;
    },
    allowCsvDownload() {
      return !this.group || this.group.allowCsvDownload !== false;
    },
    allowAudioDownload() {
      return !this.group || this.group.allowAudioDownload !== false;
    },
    // The confidence choices the group's minimum level leaves open. The API
    // already drops detections below it; this just hides choices that would
    // change nothing.
    levelOptions() {
      const options = [
        { value: "all", label: "All" },
        { value: "med", label: "Medium+" },
        { value: "high", label: "High" },
      ];
      const minLevel = this.group && this.group.minLevel;
      if (minLevel === "high") return options.slice(2);
      if (minLevel === "med") return options.slice(1);
      return options;
    },
    filteredDetections() {
      const rank = { L: 0, M: 1, H: 2 };
      const min = { all: 0, med: 1, high: 2 }[this.levelFilter];
      return this.selectedDetections.filter((d) => rank[d.level] >= min);
    },
    boxToday() {
      const tz =
        (this.selectedHaikubox && this.selectedHaikubox.timezone) ||
        moment.tz.guess();
      return moment().tz(tz).format("YYYY-MM-DD");
    },
    minDate() {
      return moment(this.boxToday).subtract(30, "days").format("YYYY-MM-DD");
    },
    groupedDetections() {
      const groups = {};

      this.filteredDetections.forEach((detection) => {
        if (!groups[detection.spCode]) {
          groups[detection.spCode] = {
            spCode: detection.spCode,
            cn: detection.cn,
            sn: detection.sn,
            detections: [],
            window: "image",
            spectrogram: null,
            spectrogramLayout: null,
            loadingSpectrogram: false,
            // Charts, built when their window is first opened
            loadingChart: false,
            hourlyCountPlotData: null,
            dailyCountPlotData: null,
            heatmap: null,
            currentDetection: null,
            // Audio properties
            audioDuration: 0,
            sampleRate: null,
            audioContext: null,
            windowOffset: 0,
            windowDuration: 3,
            windowStart: 0,
            windowEnd: 3,
            // Playback tracking
            isPlaying: false,
            playbackProgress: 0,
          };
        }
        groups[detection.spCode].detections.push(detection);
      });

      // Sort each group's detections by date (newest first)
      Object.values(groups).forEach((group) => {
        group.detections.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        // Set the first detection as current
        group.currentDetection = group.detections[0];
      });

      // Sort groups by most recent detection
      return Object.values(groups).sort(
        (a, b) =>
          new Date(b.detections[0].timestamp) -
          new Date(a.detections[0].timestamp)
      );
    },
  },
  async mounted() {
    // Initialize marker icons
    this.activeIcon = L.divIcon({
      html: '<div style="background-color: #4CAF50; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>',
      iconSize: [20, 20],
      className: "custom-div-icon",
    });

    this.inactiveIcon = L.divIcon({
      html: '<div style="background-color: #9E9E9E; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>',
      iconSize: [20, 20],
      className: "custom-div-icon",
    });

    // Load language (await so cnToSpCode is ready before loading data)
    const savedLang = localStorage.getItem("haikuboxLanguage");
    if (savedLang) {
      await this.setLanguage(savedLang);
    } else {
      const browserLang = (navigator.language || navigator.languages[0]).split("-")[0];
      await this.setLanguage(browserLang);
    }

    const slug = this.$route.params.slug;
    // A share link carries ?key=... after the '#'. Keep it, then remove it from
    // the address bar so it isn't bookmarked or copied along with the URL.
    if (this.$route.query.key) {
      storeKey(slug, this.$route.query.key);
      this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {});
    }
    this.loadGroup(slug);

    // Add click handler to close dropdowns
    document.addEventListener("click", this.handleOutsideClick);

    // Load clip seconds from localStorage
    const savedClipSeconds = localStorage.getItem("groupClipSeconds");
    if (savedClipSeconds) {
      this.clipSeconds = parseInt(savedClipSeconds);
      if (
        this.clipSeconds < 3 ||
        this.clipSeconds > 12 ||
        this.clipSeconds % 3 !== 0
      ) {
        this.clipSeconds = 3;
      }
    }
  },
  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener("click", this.handleOutsideClick);
    // Stop any playing audio
    this.stopCurrentAudio();
    // Clear audio cache
    this.clearAudioCache();
    // Close all audio contexts
    this.audioContexts.forEach((context, rate) => {
      if (context.state !== "closed") {
        context.close();
        console.log(`Closed AudioContext for ${rate}Hz`);
      }
    });
    this.audioContexts.clear();

    // Close global audio context
    if (globalAudioContext && globalAudioContext.state !== "closed") {
      globalAudioContext.close();
      globalAudioContext = null;
      globalGainNode = null;
    }
  },
  watch: {
    "$route.params.serial": function (newSerial) {
      // selectHaikubox() pushes this route itself; don't load the box twice.
      if (
        newSerial &&
        !(this.selectedHaikubox && this.selectedHaikubox.serial === newSerial)
      ) {
        this.selectHaikuboxBySerial(newSerial);
      }
    },
  },
  methods: {
    buildCnToSpCode(translations) {
      const map = {};
      for (const [key, value] of Object.entries(translations)) {
        if (typeof value === 'string' && /^[a-z0-9]+$/.test(key)) {
          map[value] = key;
        }
      }
      return map;
    },
    async setLanguage(lang) {
      if (!this.languages.includes(lang)) lang = "en";
      const [translations, enTranslations] = await Promise.all([
        this.loadTranslations(lang),
        lang === "en" ? this.loadTranslations(lang) : this.loadTranslations("en"),
      ]);
      this.currentTranslations = translations;
      this.cnToSpCode = this.buildCnToSpCode(enTranslations);
      localStorage.setItem("haikuboxLanguage", lang);
      this.selectedLanguage = lang;
    },
    loadTranslations(lang) {
      return fetch(`/I18N/${lang}.json`)
        .then((response) => response.json())
        .then((translations) => translations);
    },
    t(key, fallback) {
      return this.currentTranslations[key] || fallback || key;
    },
    async loadGroup(slug) {
      try {
        const groupData = await API.graphql({
          query: groupBySlug,
          variables: { groupSlug: slug },
          authMode: "AWS_IAM",
        });

        if (!groupData.data.groupBySlug.items.length) {
          this.$router.push("/");
          return;
        }

        this.group = groupData.data.groupBySlug.items[0];
      } catch (error) {
        console.error("Error loading group:", error);
        // Try loading from public JSON as fallback
        await this.loadGroupFromPublicJson(slug);
        return;
      }
      await this.loadGroupData(slug);
    },
    async loadGroupFromPublicJson(slug) {
      try {
        const response = await fetch(`/group-configs/${slug}.json`);
        if (!response.ok) {
          this.$router.push("/");
          return;
        }
        this.group = await response.json();
        await this.loadGroupData(slug);
      } catch (error) {
        console.error("Error loading group from JSON:", error);
        this.$router.push("/");
      }
    },
    // Everything that comes from the groups API. Called once the group's
    // metadata is loaded, and again after the access key is entered.
    async loadGroupData(slug) {
      // Start on the group's minimum level if the current choice is below it.
      if (!this.levelOptions.some((o) => o.value === this.levelFilter)) {
        this.levelFilter = this.levelOptions[0].value;
      }
      const emptySummary = {
        boxes: [],
        yesterday: { detections: 0, species: 0, topSpecies: [] },
      };
      try {
        this.summary = await getSummary(slug);
      } catch (error) {
        if (error instanceof KeyRequiredError) {
          // Still show the name, logos and map behind the prompt.
          this.summary = emptySummary;
          this.buildHaikuboxLocations();
          this.loadStatistics();
          this.groupLoaded = true;
          this.showKeyPrompt(slug);
          return;
        }
        console.error("Error loading group summary:", error);
        this.summary = emptySummary;
      }
      this.keyRequired = false;
      this.buildHaikuboxLocations();
      this.loadStatistics();

      const serial = this.$route.params.serial;
      if (serial) {
        await this.selectHaikuboxBySerial(serial);
      } else {
        await this.autoSelectFirstActiveStation();
      }
      this.groupLoaded = true;
    },
    getSerialCode(haikuboxSerial) {
      // Extract just the serial code from "Haikubox_<serial>"
      return haikuboxSerial.replace("Haikubox_", "");
    },
    buildHaikuboxLocations() {
      const boxes = {};
      for (const box of this.summary.boxes || []) {
        boxes[this.getSerialCode(box.serial)] = box;
      }

      const locations = [];
      const bounds = [];
      for (let i = 0; i < this.group.haikuboxes.length; i++) {
        const serial = this.group.haikuboxes[i];
        const serialCode = this.getSerialCode(serial);
        const lat = this.group.haikuboxLats[i];
        const lon = this.group.haikuboxLons[i];
        if (lat === undefined || lat === null || lon === undefined || lon === null) {
          console.warn(`No coordinates found for ${serial}`);
          continue;
        }
        const box = boxes[serialCode] || {};
        locations.push({
          serial,
          serialCode,
          name: serial, // fallback name
          haikuboxName: box.name || serial,
          timezone: box.timezone,
          lat: Math.round(lat * 1000) / 1000,
          lon: Math.round(lon * 1000) / 1000,
          hasRecentActivity: !!box.active24h,
        });
        bounds.push([lat, lon]);
      }
      this.haikuboxLocations = locations;

      if (bounds.length > 0) {
        this.mapBounds = latLngBounds(bounds);
        const lats = bounds.map((b) => b[0]);
        const lons = bounds.map((b) => b[1]);
        this.mapCenter = latLng(
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lons) + Math.max(...lons)) / 2
        );
        if (bounds.length === 1) {
          this.mapZoom = 12;
        }
      }
    },
    loadStatistics() {
      const yesterday = this.summary.yesterday;
      this.activeStations = this.haikuboxLocations.length;
      this.totalDetections = yesterday.detections;
      // Hidden species are already removed by the API.
      this.topSpecies = yesterday.topSpecies.slice(0, 3);
    },
    async autoSelectFirstActiveStation() {
      // Only auto-select if no station is already selected and no serial in URL
      if (!this.selectedHaikubox && !this.$route.params.serial) {
        const firstActiveStation = this.haikuboxLocations.find(
          (location) => location.hasRecentActivity
        );

        if (firstActiveStation) {
          await this.selectHaikubox(firstActiveStation);
        }
      }
    },
    async selectHaikubox(haikubox) {
      this.stopCurrentAudio();
      if (!this.selectedHaikubox || this.selectedHaikubox.serial !== haikubox.serial) {
        // Each box starts at 3s until one of its clips turns out to be long.
        this.maxClipSeconds = 3;
      }
      this.selectedHaikubox = haikubox;
      // Start on the box's local today, or keep the chosen day if it's still valid.
      if (
        !this.selectedDate ||
        this.selectedDate > this.boxToday ||
        this.selectedDate < this.minDate
      ) {
        this.selectedDate = this.boxToday;
      }

      // Update URL only if not already on this route
      const targetPath = `/groups/${this.group.groupSlug}/${haikubox.serial}`;
      if (this.$route.path !== targetPath) {
        this.$router
          .push({
            path: targetPath,
          })
          .catch((err) => {
            // Ignore NavigationDuplicated errors
            if (err.name !== "NavigationDuplicated") {
              console.error("Navigation error:", err);
            }
          });
      }

      await this.loadDetections(haikubox.serialCode);
    },
    async selectHaikuboxBySerial(serial) {
      const haikubox = this.haikuboxLocations.find((h) => h.serial === serial);
      if (haikubox) {
        await this.selectHaikubox(haikubox);
      }
    },
    async loadDetections(serialCode) {
      const request = ++this.detectionsRequest;
      this.detectionsError = "";
      this.loadingDetections = true;
      try {
        const data = await getDetections(
          this.group.groupSlug,
          serialCode,
          this.selectedDate
        );
        if (request !== this.detectionsRequest) return;
        if (data.timezone && this.selectedHaikubox && !this.selectedHaikubox.timezone) {
          this.$set(this.selectedHaikubox, "timezone", data.timezone);
        }
        this.selectedDetections = data.detections
          .filter((d) => !NON_SPECIES.has(d.cn))
          .map((d) => ({
            id: d.id,
            spCode: d.spCode,
            cn: d.cn,
            sn: d.sn,
            timestamp: d.dt,
            level: d.level,
            score: d.score,
            v3Score: d.v3Score,
            detectionCount: d.detectionCount != null ? d.detectionCount : 1,
            hasAudio: d.hasAudio,
            audioDuration: 0,
            // Minted on first play by ensureAudio().
            audioUrl: null,
            audioMintedAt: 0,
            offset: 0,
          }));
        if (data.truncated) {
          this.detectionsError =
            "This day has more detections than can be shown; showing the latest 5,000.";
        }
      } catch (error) {
        if (request !== this.detectionsRequest) return;
        this.selectedDetections = [];
        if (error instanceof KeyRequiredError) {
          this.showKeyPrompt(this.group.groupSlug);
        } else {
          console.error("Error loading detections:", error);
          this.detectionsError = error.message;
        }
      } finally {
        if (request === this.detectionsRequest) this.loadingDetections = false;
      }
    },
    boxTimezone() {
      return (
        (this.selectedHaikubox && this.selectedHaikubox.timezone) ||
        moment.tz.guess()
      );
    },
    passesLevelFilter(level) {
      const rank = { L: 0, M: 1, H: 2 };
      const min = { all: 0, med: 1, high: 2 }[this.levelFilter];
      return rank[level] >= min;
    },
    // Haikubox.vue decides this from the wav path, which the groups API
    // doesn't expose. Once one clip has played longer than 3s
    // (maxClipSeconds > 3) the box's clips are all long, so every card gets
    // the chip, except bats, which always play in full.
    isLongClipFile(detection) {
      return !!detection && detection.cn !== "Bat";
    },
    // Dropdown entry, in Haikubox.vue's format: confidence letter, then time.
    detectionListName(detection) {
      return `${detection.level} ${moment
        .utc(detection.timestamp)
        .tz(this.boxTimezone())
        .locale(this.selectedLanguage)
        .format("ll LTS")}`;
    },
    isBat(speciesGroup) {
      return speciesGroup.spCode === "batbatbat" || speciesGroup.cn === "Bat";
    },
    updateClipSeconds(delta) {
      const newValue = this.clipSeconds + delta;
      if (newValue >= 3 && newValue <= this.maxClipSeconds) {
        this.clipSeconds = newValue;
        localStorage.setItem("groupClipSeconds", this.clipSeconds.toString());
      }
    },
    // Switch a card's display, building its chart the first time it's shown.
    async setWindowRecent(speciesGroup, value) {
      if (value === "spectrogram" || value === "image") {
        await this.setWindowSpecies(speciesGroup, value);
        return;
      }
      speciesGroup.window = value;
      speciesGroup.loadingChart = true;
      this.$forceUpdate();
      try {
        if (value === "daily") {
          speciesGroup.hourlyCountPlotData = this.getHourlySpecies(speciesGroup);
        } else if (value === "monthly") {
          speciesGroup.dailyCountPlotData = await this.getDailySpeciesChart(
            speciesGroup.cn
          );
        } else if (value === "heatmap") {
          speciesGroup.heatmap = await this.getHeatmap(speciesGroup.cn);
        }
      } catch (error) {
        if (error instanceof KeyRequiredError) {
          this.showKeyPrompt(this.group.groupSlug);
        }
        console.error(`Error building ${value} chart:`, error);
        speciesGroup.window = "image";
      } finally {
        speciesGroup.loadingChart = false;
        this.$forceUpdate();
      }
    },
    // Donut of the selected day's detections by local hour, styled like the
    // Haikubox.vue "Past 24h" chart. Uses the card's detections, so the
    // confidence filter applies.
    getHourlySpecies(speciesGroup) {
      const tz = this.boxTimezone();
      const hourly = Array(24).fill(0);
      for (const d of speciesGroup.detections) {
        hourly[moment.utc(d.timestamp).tz(tz).hour()] += d.detectionCount;
      }

      const colorMap = [
        "#ffffd9",
        "#edf8b1",
        "#c7e9b4",
        "#7fcdbb",
        "#41b6c4",
        "#1d91c0",
        "#225ea8",
        "#0c2c84",
      ];
      const hourLabel = (h) =>
        h === 0 ? "midnight" : h === 12 ? "noon" : h < 12 ? `${h}am` : `${h - 12}pm`;
      const isToday = this.selectedDate === this.boxToday;
      const currentHour = moment().tz(tz).hour();

      let total = 0;
      const plotData = [];
      // Same slice order as Haikubox.vue: newest hour first, going backwards.
      for (let h = 23; h >= 0; h--) {
        const count = hourly[h];
        let colorIndex = 0;
        if (count > 0) colorIndex = 1;
        if (count > 1) colorIndex = 2;
        if (count > 2) colorIndex = 3;
        if (count > 5) colorIndex = 4;
        if (count > 10) colorIndex = 5;
        if (count > 15) colorIndex = 6;
        if (count > 20) colorIndex = 7;
        total += count;
        plotData.push({
          label: hourLabel(h),
          value: 100,
          color: colorMap[colorIndex],
          showLabel: h % 6 === 0 ? 1 : 0,
          isSliced: isToday && h === currentHour ? 1 : 0,
          tooltext: `${hourLabel(h)}:${count}`,
        });
      }

      // The first slice is 11pm; this puts midnight at the top.
      let startingAngle = 260 - 23 * (360 / 24);
      if (startingAngle < 0) startingAngle += 360;

      return {
        chart: {
          caption: isToday
            ? this.t("Today")
            : moment(this.selectedDate).format("MMM D, YYYY"),
          numberPrefix: "",
          bgColor: "#ffffff",
          startingAngle: startingAngle.toString(),
          showLegend: "0",
          enableSmartLabels: "1",
          showLabels: "0",
          showValues: "0",
          defaultCenterLabel: total.toString(),
          centerLabelFontSize: "18",
          centerLabelBold: "1",
          enableMultiSlicing: "0",
          animateClockwise: "1",
          showPercentInToolTip: "0",
        },
        data: plotData,
      };
    },
    // Daily counts for the past 3 months, as [MM-DD, count] for column-chart.
    async getDailySpeciesChart(cn) {
      const serialCode = this.selectedHaikubox.serialCode;
      const cacheKey = `${serialCode}|${cn}`;
      let data = this.dailyCountsCache.get(cacheKey);
      if (!data) {
        data = await getDailyCounts(this.group.groupSlug, serialCode, {
          weeks: 13,
          cn,
        });
        this.dailyCountsCache.set(cacheKey, data);
      }
      const species = (data.species || []).find((s) => s.cn === cn);
      const byDate = {};
      for (const [date, count, , med, high] of species ? species.days : []) {
        // Older firmware doesn't record the level split (null); use the total.
        if (this.levelFilter === "high" && high !== null) byDate[date] = high;
        else if (this.levelFilter === "med" && med !== null) byDate[date] = med + high;
        else byDate[date] = count;
      }

      const plotData = [];
      const end = moment(this.boxToday);
      for (let day = moment(end).subtract(90, "days"); day <= end; day.add(1, "days")) {
        plotData.push([day.format("MM-DD"), byDate[day.format("YYYY-MM-DD")] || 0]);
      }
      return plotData;
    },
    // One local day's detections for the selected box: the loaded list for
    // the selected day, otherwise fetched (and cached if the day is over).
    async getDayDetections(date) {
      if (date === this.selectedDate) return this.selectedDetections;
      const serialCode = this.selectedHaikubox.serialCode;
      const cacheKey = `${serialCode}|${date}`;
      if (this.dayDetectionsCache.has(cacheKey)) {
        return this.dayDetectionsCache.get(cacheKey);
      }
      const data = await getDetections(this.group.groupSlug, serialCode, date);
      const detections = data.detections
        .filter((d) => !NON_SPECIES.has(d.cn))
        .map((d) => ({
          cn: d.cn,
          sn: d.sn,
          spCode: d.spCode,
          timestamp: d.dt,
          level: d.level,
          score: d.score,
          v3Score: d.v3Score,
          detectionCount: d.detectionCount != null ? d.detectionCount : 1,
        }));
      if (date < this.boxToday) this.dayDetectionsCache.set(cacheKey, detections);
      return detections;
    },
    // CSV downloads, following the Haikubox ALL page. Summary and daily
    // counts cover the API's 52 weeks; raw detections its last 30 days.
    async downloadCSV() {
      const dates = [...this.csvDates].sort();
      if (this.csvRaw && dates.length === 1) dates.push(dates[0]);
      this.csvDialog = false;
      this.csvLoading = true;

      const slug = this.group.groupSlug;
      const serialCode = this.selectedHaikubox.serialCode;
      const baseName = `${slug}_${this.selectedHaikubox.haikuboxName || serialCode}`;
      try {
        if (this.csvSummary || this.csvDailyCounts) {
          const data = await getDailyCounts(slug, serialCode, { weeks: 52 });
          const species = data.species || [];

          if (this.csvSummary) {
            const rows = species
              .map((sp) => ({
                name: this.t(sp.spCode, sp.cn),
                total: sp.days.reduce((sum, day) => sum + (day[1] || 0), 0),
              }))
              .sort((a, b) => b.total - a.total)
              .map((sp) => [sp.name, sp.total]);
            this.saveCsv(`${baseName}.csv`, ["Species", "Count"], rows);
          }

          if (this.csvDailyCounts) {
            // daily-counts has no scientific names; species-list.json does.
            const scientificNames = await this.loadScientificNames();
            const rows = [];
            for (const sp of species) {
              const sn = scientificNames[sp.spCode] || "";
              for (const [date, count] of sp.days) {
                rows.push([date, this.t(sp.spCode, sp.cn), sn, count]);
              }
            }
            rows.sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0));
            this.saveCsv(
              `${baseName}_${this.t("csv_daily_counts", "daily_counts")}.csv`,
              ["Date", "Species", "Scientific Name", "Count"],
              rows
            );
          }
        }

        if (this.csvRaw) {
          const days = [];
          for (
            let day = moment(dates[0]);
            day.format("YYYY-MM-DD") <= dates[1];
            day.add(1, "days")
          ) {
            days.push(day.format("YYYY-MM-DD"));
          }
          const perDay = await Promise.all(days.map((date) => this.getDayDetections(date)));
          const tz = this.boxTimezone();
          const detections = [].concat(...perDay);
          // Newer detections also carry a BirdNET v3 score. When any do, the
          // main score is labelled HaikuNet and v3 gets its own column.
          const hasV3 = detections.some(
            (d) => d.v3Score !== undefined && d.v3Score !== null
          );
          const rows = [];
          for (const d of detections) {
            const local = moment.utc(d.timestamp).tz(tz);
            rows.push([
              this.t(d.spCode, d.cn),
              d.sn,
              d.timestamp,
              local.format("DD-MMM-YYYY"),
              local.format("HH:mm:ss"),
              d.score,
              ...(hasV3 ? [d.v3Score] : []),
              d.level,
              d.detectionCount,
            ]);
          }
          rows.sort((a, b) => (a[2] < b[2] ? 1 : a[2] > b[2] ? -1 : 0));
          this.saveCsv(
            `${baseName}_${this.t("csv_raw", "raw")}.csv`,
            [
              "Species",
              "Scientific Name",
              "UTC Date",
              "Local Date",
              "Local Time",
              ...(hasV3 ? ["HaikuNet score", "BirdNETv3 score"] : ["Score"]),
              "Level",
              "Count",
            ],
            rows
          );
        }
      } catch (error) {
        if (error instanceof KeyRequiredError) {
          this.showKeyPrompt(slug);
        } else {
          console.error("Error building CSV:", error);
          alert("Couldn't build the CSV. Please try again.");
        }
      } finally {
        this.csvLoading = false;
      }
    },
    // spCode → scientific name, from the model's species list. Fetched once.
    loadScientificNames() {
      if (!this.scientificNamesLoaded) {
        this.scientificNamesLoaded = fetch("/species-list.json")
          .then((r) => r.json())
          .then((list) => {
            const names = {};
            for (const [spCode, , sn] of list) names[spCode] = sn;
            return names;
          })
          .catch((error) => {
            console.error("Error loading species list:", error);
            this.scientificNamesLoaded = null;
            return {};
          });
      }
      return this.scientificNamesLoaded;
    },
    saveCsv(filename, header, rows) {
      // Quote cells that need it; species names can contain commas.
      const cell = (value) => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const csv = [header, ...rows].map((row) => row.map(cell).join(",")).join("\n") + "\n";
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.setAttribute("href", url);
      a.setAttribute("download", filename.replace(/[^\p{L}\p{N}._-]+/gu, "_"));
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    // Calls per hour over the 14 days ending on the selected day, as in
    // Haikubox.vue. Days older than the API's 30-day window stay empty.
    async getHeatmap(cn) {
      const nDays = 14;
      const tz = this.boxTimezone();
      const dates = [];
      for (let i = nDays - 1; i >= 0; i--) {
        dates.push(moment(this.selectedDate).subtract(i, "days").format("YYYY-MM-DD"));
      }

      const days = await Promise.all(
        dates.map((date) =>
          date < this.minDate
            ? []
            : this.getDayDetections(date).catch((error) => {
                if (error instanceof KeyRequiredError) throw error;
                console.error(`Error loading detections for ${date}:`, error);
                return [];
              })
        )
      );

      const heatmapData = [];
      for (let h = 0; h < 24; h++) heatmapData.push(Array(nDays).fill(0));
      days.forEach((detections, dayBin) => {
        for (const d of detections) {
          if (d.cn !== cn || !this.passesLevelFilter(d.level)) continue;
          heatmapData[moment.utc(d.timestamp).tz(tz).hour()][dayBin] += d.detectionCount;
        }
      });

      return [
        {
          x: dates.map((date) => moment(date).format("MMM D")),
          y: Array.from({ length: 24 }, (_, h) => `${h}:00`),
          z: heatmapData,
          type: "heatmap",
          showscale: true,
          hoverongaps: false,
        },
      ];
    },
    onDateChange() {
      this.dateMenu = false;
      this.stopCurrentAudio();
      this.loadDetections(this.selectedHaikubox.serialCode);
    },
    // The list carries no audio URLs; request one the first time a detection
    // plays, and a fresh one once it's close to the 1-hour expiry.
    async ensureAudio(detection) {
      if (!detection.hasAudio) return false;
      const fresh =
        detection.audioUrl && Date.now() - detection.audioMintedAt < 50 * 60 * 1000;
      if (fresh) return true;
      try {
        const audio = await getAudio(
          this.group.groupSlug,
          this.selectedHaikubox.serialCode,
          detection.id
        );
        if (detection.audioUrl) this.audioCache.delete(detection.audioUrl);
        this.$set(detection, "audioUrl", audio.url);
        detection.offset = audio.offset;
        detection.audioMintedAt = Date.now();
        return true;
      } catch (error) {
        if (error instanceof KeyRequiredError) {
          this.showKeyPrompt(this.group.groupSlug);
        }
        console.error("Error getting audio URL:", error);
        return false;
      }
    },
    async downloadAudio(detection) {
      try {
        const audio = await getAudio(
          this.group.groupSlug,
          this.selectedHaikubox.serialCode,
          detection.id,
          { download: true }
        );
        window.location.href = audio.url;
      } catch (error) {
        console.error("Error downloading audio:", error);
        alert("Audio not available for this detection");
      }
    },
    showKeyPrompt(slug) {
      // A stored key that was rejected is stale (rotated or removed); drop it.
      if (getStoredKey(slug)) {
        storeKey(slug, null);
        if (!this.keyError) {
          this.keyError = this.t("That key didn't work. It may have been changed.");
        }
      }
      this.keyRequired = true;
    },
    async submitKey() {
      const slug = this.$route.params.slug;
      storeKey(slug, this.keyInput.trim());
      this.keyError = "";
      this.keyRequired = false;
      await this.loadGroupData(slug);
      if (this.keyRequired) {
        this.keyError = this.t("That key didn't work.");
      } else {
        this.keyInput = "";
      }
    },
    getLogoUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;

      const bucketName = "haikubox-media152750-prod";

      // Handle both with and without 'public/' prefix
      if (path.startsWith("public/")) {
        return `https://${bucketName}.s3.amazonaws.com/${path}`;
      } else if (path.startsWith("groups/")) {
        return `https://${bucketName}.s3.amazonaws.com/public/${path}`;
      } else {
        return `https://${bucketName}.s3.amazonaws.com/public/${path}`;
      }
    },
    getImageUrl(spCode) {
      return `https://haikubox-images.s3.amazonaws.com/${spCode}.jpeg?v=5`;
    },
    onImageError(spCode) {
      this.failedImages.add(spCode);
      this.$forceUpdate();
    },
    formatTime(timestamp) {
      return moment(timestamp).format("MMM D, h:mm A");
    },
    formatDetectionCount(count) {
      if (count > 99999) {
        return Math.floor(count / 1000) + "K+";
      } else if (count > 9999) {
        return (count / 1000).toFixed(1) + "K";
      }
      return count.toString();
    },
    zoomUpdate(zoom) {
      this.mapZoom = zoom;
    },
    centerUpdate(center) {
      this.mapCenter = center;
    },

    // Enhanced audio methods from Notifications.vue
    initAudioContext() {
      if (globalAudioContext == null) {
        const isIOS = /iPhone|iPod|iPad/.test(navigator.userAgent);
        if (isIOS) {
          globalAudioContext = new AudioContext();
          // Note: audioContext, allowBackgroundPlayback, forceIOSBehavior
          unmute(globalAudioContext, false, false);
        } else {
          globalAudioContext = new AudioContext();
        }
        globalGainNode = globalAudioContext.createGain();
        globalGainNode.gain.value = this.playbackGain;
        globalGainNode.connect(globalAudioContext.destination);
      }
    },

    // Helper to get audio file's native sample rate
    async getAudioSampleRate(audioUrl, arrayBuffer) {
      // Create a temporary context just to decode and get the sample rate
      const tempContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      try {
        const audioBuffer = await this.decodeAudioData(
          tempContext,
          arrayBuffer.slice(0)
        );
        const sampleRate = audioBuffer.sampleRate;
        tempContext.close();
        return sampleRate;
      } catch (error) {
        tempContext.close();
        throw error;
      }
    },

    // Get or create AudioContext matching the file's sample rate
    async getMatchingAudioContext(sampleRate) {
      // Check if we already have a context for this sample rate
      if (this.audioContexts.has(sampleRate)) {
        const context = this.audioContexts.get(sampleRate);
        if (context.state !== "closed") {
          return context;
        }
      }

      // Create new context with matching sample rate
      try {
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: sampleRate,
        });
        this.audioContexts.set(sampleRate, audioContext);
        return audioContext;
      } catch (error) {
        console.warn(`Cannot create context at ${sampleRate}Hz, using default`);
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        this.audioContexts.set("default", audioContext);
        return audioContext;
      }
    },

    async detectFlacSampleRate(arrayBuffer) {
      try {
        const view = new DataView(arrayBuffer);

        // Verify it's a FLAC file
        const magic = String.fromCharCode(
          view.getUint8(0),
          view.getUint8(1),
          view.getUint8(2),
          view.getUint8(3)
        );

        if (magic !== "fLaC") {
          console.warn("Not a FLAC file");
          return null;
        }

        // STREAMINFO block starts at byte 8
        // Sample rate is 20 bits starting at bit 80 (byte 18)
        const byte18 = view.getUint8(18);
        const byte19 = view.getUint8(19);
        const byte20 = view.getUint8(20);

        // Sample rate is 20 bits: all of byte18, all of byte19, top 4 bits of byte20
        const sampleRate = (byte18 << 12) | (byte19 << 4) | (byte20 >> 4);

        return sampleRate;
      } catch (error) {
        console.error("Error detecting FLAC sample rate:", error);
        return null;
      }
    },

    // Simplified generateSpectrogram
    async generateSpectrogram(speciesGroup, audioUrl) {
      this.$set(speciesGroup, "loadingSpectrogram", true);

      try {
        let audioBuffer;
        let audioContext;

        const cachedData = this.audioCache.get(audioUrl);

        if (cachedData && cachedData.audioBuffer) {
          audioBuffer = cachedData.audioBuffer;
          audioContext = cachedData.audioContext;
        } else {
          // Fetch the audio file
          const response = await fetch(audioUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const arrayBuffer = await response.arrayBuffer();

          // Detect the ACTUAL native sample rate from FLAC header
          const nativeSampleRate = await this.detectFlacSampleRate(arrayBuffer);

          if (!nativeSampleRate) {
            // Fallback to default context if can't detect
            audioContext = new (window.AudioContext ||
              window.webkitAudioContext)();
            console.warn("Could not detect sample rate, using default context");
          } else {
            // Create context with the ACTUAL native sample rate
            audioContext = await this.getMatchingAudioContext(nativeSampleRate);
          }

          // Now decode with the matching context
          audioBuffer = await this.decodeAudioData(
            audioContext,
            arrayBuffer.slice(0)
          );

          // Cache everything
          this.audioCache.set(audioUrl, {
            arrayBuffer: arrayBuffer,
            audioBuffer: audioBuffer,
            audioContext: audioContext,
            nativeSampleRate: nativeSampleRate, // Store the detected rate
          });
        }

        // Store references in species group
        speciesGroup.audioContext = audioContext;
        speciesGroup.audioDuration = audioBuffer.duration;
        speciesGroup.sampleRate = audioBuffer.sampleRate;
        // As in Haikubox.vue: a clip longer than 3s means the box has long clips.
        if (audioBuffer.duration > 3 && this.maxClipSeconds <= 3) {
          this.maxClipSeconds = 12;
        }

        // Set window parameters
        if (audioBuffer.duration <= 3) {
          speciesGroup.windowOffset = 0;
          speciesGroup.windowDuration = audioBuffer.duration;
        } else {
          speciesGroup.windowDuration = 12; // Always set to 12 for longer audio
          const detection =
            speciesGroup.currentDetection || speciesGroup.detections[0];
          if (detection.offset && !speciesGroup.windowOffset) {
            // Center the 12-second window around the detection, ensuring it stays within bounds
            const halfWindow = 6; // Half of 12 seconds
            const idealOffset = Math.max(0, detection.offset - halfWindow);
            speciesGroup.windowOffset = Math.min(
              idealOffset,
              Math.max(0, audioBuffer.duration - 12)
            );
          }
        }

        // Generate spectrogram
        await this.generateSpectrogramForWindow(speciesGroup, audioBuffer);
      } catch (error) {
        console.error("Error generating spectrogram:", error);
        speciesGroup.spectrogram = null;
      } finally {
        speciesGroup.loadingSpectrogram = false;
        this.$forceUpdate();
      }
    },

    async generateSpectrogramForWindow(speciesGroup, audioBuffer) {
      // console.log("generateSpectrogramForWindow called", {
      //   duration: audioBuffer.duration,
      //   sampleRate: audioBuffer.sampleRate,
      //   hasExistingSpectrogram: !!speciesGroup.spectrogram,
      // });

      const signal = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;

      // Dynamic FFT length based on sample rate
      const fftLength = this.getOptimalFFTLength(sampleRate);
      const halfLength = fftLength / 2;

      // Control how much of the spectrum to show (e.g., 0.8 = 80% of Nyquist)
      const frequencyCutoff = 0.8;
      const showLength = Math.floor(halfLength * frequencyCutoff);

      // For scrolling spectrogram, always generate the full duration
      const startSample = 0;
      const endSample = signal.length;

      // Extract signal
      const windowedSignal = signal.slice(startSample, endSample);

      // FFT parameters
      const overlap = 0.5;
      const hopSize = Math.floor(fftLength * (1.0 - overlap));
      const numTimeSteps =
        Math.floor((windowedSignal.length - fftLength) / hopSize) + 1;
      const fft = new FFT(fftLength, sampleRate);

      // Generate Hann window
      const hannWindow = this.generateHannWindow(fftLength);

      // Generate spectrogram - store as 2D array directly
      const spectrogramData = [];
      const allValues = [];

      // Initialize the 2D array
      for (let i = 0; i < showLength; i++) {
        spectrogramData[i] = [];
      }

      // Process each time window
      for (let timeStep = 0; timeStep < numTimeSteps; timeStep++) {
        const startIdx = timeStep * hopSize;
        const chunk = windowedSignal.slice(startIdx, startIdx + fftLength);

        // Pad with zeros if needed (for the last chunk)
        while (chunk.length < fftLength) {
          chunk.push(0);
        }

        // Apply Hann window
        for (let i = 0; i < fftLength; i++) {
          chunk[i] = chunk[i] * hannWindow[i];
        }

        // Perform FFT
        fft.forward(chunk);

        const scaleFactor = fftLength / 2;

        // Convert to dB and store only up to showLength
        for (let freqBin = 0; freqBin < showLength; freqBin++) {
          const value = fft.spectrum[freqBin] * scaleFactor;
          const db = 20 * Math.log10(Math.max(value, 1e-10));
          spectrogramData[freqBin][timeStep] = db;
          allValues.push(db);
        }
      }

      // Calculate percentiles for color scaling
      allValues.sort((a, b) => a - b);
      const percentile = (p) => {
        const index = Math.floor((allValues.length * p) / 100);
        return allValues[Math.min(index, allValues.length - 1)];
      };

      const minVal = percentile(30);
      const maxVal = percentile(99);
      const dynamicRange = maxVal - minVal;

      // Clamp values in place
      for (let i = 0; i < showLength; i++) {
        for (let j = 0; j < numTimeSteps; j++) {
          spectrogramData[i][j] = Math.max(
            minVal,
            Math.min(maxVal, spectrogramData[i][j])
          );
        }
      }

      if (speciesGroup.spCode != "tursiopstrunc") {
        // Gaussian smoothing
        for (let i = 1; i < showLength - 1; i++) {
          for (let j = 1; j < numTimeSteps - 1; j++) {
            // Simple 3x3 Gaussian kernel
            const smoothed =
              spectrogramData[i][j] * 0.4 +
              (spectrogramData[i - 1][j] +
                spectrogramData[i + 1][j] +
                spectrogramData[i][j - 1] +
                spectrogramData[i][j + 1]) *
                0.15;
            spectrogramData[i][j] = smoothed;
          }
        }
      }

      // Store full spectrogram data and parameters
      speciesGroup.fullSpectrogramData = spectrogramData;
      speciesGroup.totalTimeSteps = numTimeSteps;
      speciesGroup.totalDuration = audioBuffer.duration;
      speciesGroup.timePerStep = audioBuffer.duration / numTimeSteps;
      speciesGroup.minVal = minVal;
      speciesGroup.maxVal = maxVal;
      speciesGroup.dynamicRange = dynamicRange;
      speciesGroup.frequencyCutoff = frequencyCutoff;
      speciesGroup.showLength = showLength;
      speciesGroup.sampleRate = sampleRate;

      // Calculate initial window (3 seconds or full duration if shorter)
      const windowDuration = Math.min(3, audioBuffer.duration);
      const windowTimeSteps = Math.floor(
        numTimeSteps * (windowDuration / audioBuffer.duration)
      );

      // Store window parameters
      speciesGroup.windowDuration = windowDuration;
      speciesGroup.windowTimeSteps = windowTimeSteps;
      speciesGroup.currentWindowStart = 0;

      // Extract initial window data
      const windowData = this.extractWindowData(
        speciesGroup,
        0,
        windowTimeSteps
      );

      // Create custom layout for 3s window
      const customLayout = {
        ...this.plotlyLayout,
        yaxis: {
          ...this.plotlyLayout.yaxis,
          range: [0, showLength],
          showticklabels: false,
          showgrid: false,
          zeroline: false,
          showline: false,
          fixedrange: true,
        },
        xaxis: {
          ...this.plotlyLayout.xaxis,
          title: "",
          showticklabels: false, // times are shown under the plot instead
          range: [0, windowTimeSteps],
          tickmode: "array",
          tickvals: [
            0,
            windowTimeSteps * 0.25,
            windowTimeSteps * 0.5,
            windowTimeSteps * 0.75,
            windowTimeSteps - 1,
          ],
          ticktext: ["0", "0.75", "1.5", "2.25", "3"],
          fixedrange: true,
        },
      };

      // Improved colorscale
      const colorscale = [
        [0, "#440154"], // Dark purple
        [0.3, "#3b528b"], // Dark blue
        [0.7, "#21908c"], // Teal
        [0.8, "#5dc863"], // Green
        [0.9, "#fde725"], // Yellow
        [1, "#ffffff"], // White for peaks
      ];

      // Calculate actual displayed frequency range for hover
      const maxFreq = (sampleRate / 2) * frequencyCutoff;
      const freqPerBin = maxFreq / showLength;
      speciesGroup.maxFreqKHz = Math.round(maxFreq / 1000);

      // Use Vue's $set to ensure reactivity
      this.$set(speciesGroup, "spectrogram", [
        {
          z: windowData,
          type: "heatmap",
          colorscale: colorscale,
          showscale: false,
          zmin: minVal,
          zmax: maxVal,
          zmid: minVal + dynamicRange * 0.9,
          hovertemplate:
            "Freq: %{customdata:.1f}kHz<br>Time: %{x:.2f}s<br>Power: %{z:.1f}dB<extra></extra>",
          customdata: Array.from(
            { length: showLength },
            (_, i) => (i * freqPerBin) / 1000
          ),
          zsmooth: false,
          connectgaps: false,
        },
      ]);

      this.$set(speciesGroup, "spectrogramLayout", customLayout);

      console.log("Spectrogram data set successfully");
    },

    // Add this new helper method to extract window data
    extractWindowData(speciesGroup, startStep, endStep) {
      const windowData = [];
      for (let i = 0; i < speciesGroup.showLength; i++) {
        windowData[i] = speciesGroup.fullSpectrogramData[i].slice(
          startStep,
          endStep
        );
      }
      return windowData;
    },

    // Add this new method to update the spectrogram window during playback
    updateSpectrogramWindowDuringPlayback(
      speciesGroup,
      playbackTime,
      startTime
    ) {
      if (
        !speciesGroup.fullSpectrogramData ||
        speciesGroup.totalDuration <= 3
      ) {
        return; // No need to scroll for short audio
      }

      // Ensure spectrogram exists before trying to update it
      if (!speciesGroup.spectrogram || !speciesGroup.spectrogram[0]) {
        return; // Spectrogram not ready yet
      }

      // Calculate the center of the 3-second window based on current playback position
      const currentTime = startTime + playbackTime;
      let windowStartTime = currentTime - 1.5; // Center the 3s window

      // Clamp to valid range
      windowStartTime = Math.max(
        0,
        Math.min(windowStartTime, speciesGroup.totalDuration - 3)
      );

      // Convert time to timestep indices
      const windowStartStep = Math.floor(
        (windowStartTime / speciesGroup.totalDuration) *
          speciesGroup.totalTimeSteps
      );
      const windowEndStep = Math.min(
        windowStartStep + speciesGroup.windowTimeSteps,
        speciesGroup.totalTimeSteps
      );

      // Extract window data
      const windowData = this.extractWindowData(
        speciesGroup,
        windowStartStep,
        windowEndStep
      );

      // Update the spectrogram data using Vue's $set for reactivity
      if (speciesGroup.spectrogram && speciesGroup.spectrogram[0]) {
        this.$set(speciesGroup.spectrogram[0], "z", windowData);
      }

      // Update the x-axis tick labels to show actual time
      speciesGroup.spectrogramLayout = {
        ...speciesGroup.spectrogramLayout,
        xaxis: {
          ...speciesGroup.spectrogramLayout.xaxis,
          tickvals: [
            0,
            speciesGroup.windowTimeSteps * 0.25,
            speciesGroup.windowTimeSteps * 0.5,
            speciesGroup.windowTimeSteps * 0.75,
            speciesGroup.windowTimeSteps - 1,
          ],
          ticktext: [
            windowStartTime.toFixed(1),
            (windowStartTime + 0.75).toFixed(1),
            (windowStartTime + 1.5).toFixed(1),
            (windowStartTime + 2.25).toFixed(1),
            (windowStartTime + 3).toFixed(1),
          ],
        },
      };

      // Update the progress bar position relative to the window
      const relativeProgress = ((currentTime - windowStartTime) / 3) * 100;
      speciesGroup.playbackProgress = Math.max(
        0,
        Math.min(100, relativeProgress)
      );

      // Store current window position
      speciesGroup.currentWindowStart = windowStartTime;
    },

    // Show one window of a long clip's spectrogram. Replaces the data and
    // layout objects (as the scroll does) so the card's own plot redraws.
    showSpectrogramWindow(speciesGroup, windowData, windowStartTime) {
      if (speciesGroup.spectrogram && speciesGroup.spectrogram[0]) {
        this.$set(speciesGroup.spectrogram[0], "z", windowData);
      }
      if (speciesGroup.spectrogramLayout) {
        speciesGroup.spectrogramLayout = {
          ...speciesGroup.spectrogramLayout,
          xaxis: { ...speciesGroup.spectrogramLayout.xaxis },
        };
      }
      speciesGroup.currentWindowStart = windowStartTime;
    },

    // Replace the existing playFile method with this enhanced version:
    async playFile(audioFile, detection) {
      this.initAudioContext();

      if (globalAudioContext.state === "suspended") {
        globalAudioContext.resume();
      }

      globalGainNode.gain.value = this.playbackGain;

      // Stop any currently playing audio
      this.stopCurrentAudio();

      try {
        // Fetch audio
        const response = await fetch(audioFile);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await globalAudioContext.decodeAudioData(
          arrayBuffer
        );

        // As in Haikubox.vue: once any loaded clip is longer than 3s, allow 12s.
        if (!detection.audioDuration) {
          detection.audioDuration = audioBuffer.duration;
          const hasLongFile = this.selectedDetections.some(
            (d) => d.audioDuration && d.audioDuration > 3
          );
          if (hasLongFile && this.maxClipSeconds <= 3) {
            this.maxClipSeconds = 12;
          }
        }

        const sampleSource = globalAudioContext.createBufferSource();

        // Apply filters similar to the original implementation
        const highPassFilter = globalAudioContext.createBiquadFilter();
        highPassFilter.type = "highpass";
        highPassFilter.frequency.value = 100;
        highPassFilter.Q.value = 1;

        const lowPassFilter = globalAudioContext.createBiquadFilter();
        lowPassFilter.type = "lowpass";
        lowPassFilter.frequency.value = 10000;
        lowPassFilter.Q.value = 1;

        // Analyze and normalize
        let maxSample = 0;
        for (
          let channel = 0;
          channel < audioBuffer.numberOfChannels;
          channel++
        ) {
          const channelData = audioBuffer.getChannelData(channel);
          for (let i = 0; i < channelData.length; i++) {
            maxSample = Math.max(maxSample, Math.abs(channelData[i]));
          }
        }

        const gainNode = globalAudioContext.createGain();
        if (maxSample > 0.00001) {
          const targetLevel = 0.9;
          const requiredGain = targetLevel / maxSample;
          const appliedGain = Math.min(requiredGain, 400);
          gainNode.gain.value = appliedGain;
        }

        // Connect audio chain
        sampleSource.connect(highPassFilter);
        highPassFilter.connect(lowPassFilter);
        lowPassFilter.connect(gainNode);
        gainNode.connect(globalGainNode);

        // Calculate playback parameters
        let startTime = 0;
        let playbackDuration = audioBuffer.duration;

        if (audioBuffer.duration > 3) {
          const offset = detection.offset || 0;

          if (offset > 0) {
            startTime = offset - Math.floor(this.clipSeconds / 2);
          } else {
            startTime = 0;
          }

          if (startTime < 0) startTime = 0;
          if (startTime + this.clipSeconds > audioBuffer.duration) {
            startTime = audioBuffer.duration - this.clipSeconds;
          }

          playbackDuration = this.clipSeconds;
        }

        if (startTime < 0) startTime = 0; // file shorter than clipSeconds

        // Cut the clip out and ramp its ends (cos², 5ms) so it doesn't click,
        // as Haikubox.vue does.
        const startSample = Math.floor(startTime * audioBuffer.sampleRate);
        const endSample = Math.min(
          Math.floor((startTime + playbackDuration) * audioBuffer.sampleRate),
          audioBuffer.length
        );
        const clipBuffer = globalAudioContext.createBuffer(
          audioBuffer.numberOfChannels,
          Math.max(1, endSample - startSample),
          audioBuffer.sampleRate
        );
        for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
          const segment = audioBuffer
            .getChannelData(ch)
            .slice(startSample, endSample);
          this.applyCos2Ramp(segment, audioBuffer.sampleRate, 5);
          clipBuffer.getChannelData(ch).set(segment);
        }
        sampleSource.buffer = clipBuffer;
        sampleSource.start(0);
        this.currentAudioSource = sampleSource;

        // Track playback progress with scrolling spectrogram
        const speciesGroup = this.groupedDetections.find((group) =>
          group.detections.some((d) => d.id === detection.id)
        );

        if (speciesGroup) {
          speciesGroup.isPlaying = true;
          speciesGroup.playbackProgress = 0;
          this.playbackStartTime = globalAudioContext.currentTime;
          this.playbackDuration = playbackDuration;

          // For scrolling spectrogram
          const playbackStartOffset = startTime;

          // Set initial window position for spectrogram
          if (audioBuffer.duration > 3) {
            this.updateSpectrogramWindowDuringPlayback(
              speciesGroup,
              0,
              playbackStartOffset
            );
          }

          this.playbackInterval = setInterval(() => {
            if (!globalAudioContext || !this.playbackStartTime) {
              clearInterval(this.playbackInterval);
              return;
            }
            const elapsed =
              globalAudioContext.currentTime - this.playbackStartTime;

            if (elapsed >= playbackDuration) {
              this.stopCurrentAudio();
              speciesGroup.isPlaying = false;
              speciesGroup.playbackProgress = 0;

              // Reset spectrogram view
              if (
                audioBuffer.duration > 3 &&
                speciesGroup.fullSpectrogramData
              ) {
                // Reset to show first 3 seconds
                const windowData = this.extractWindowData(
                  speciesGroup,
                  0,
                  speciesGroup.windowTimeSteps
                );
                this.showSpectrogramWindow(speciesGroup, windowData, 0);
                speciesGroup.spectrogramLayout.xaxis.ticktext = [
                  "0",
                  "0.75",
                  "1.5",
                  "2.25",
                  "3",
                ];
              }
            } else {
              // Update scrolling spectrogram window
              if (audioBuffer.duration > 3) {
                this.updateSpectrogramWindowDuringPlayback(
                  speciesGroup,
                  elapsed,
                  playbackStartOffset
                );
              } else {
                // For short audio, just update progress normally
                const progress = (elapsed / playbackDuration) * 100;
                speciesGroup.playbackProgress = progress;
              }
            }
            this.$forceUpdate();
          }, 50); // Update every 50ms for smooth scrolling
        }

        sampleSource.onended = () => {
          if (speciesGroup) {
            speciesGroup.isPlaying = false;
            speciesGroup.playbackProgress = 0;

            // Reset spectrogram view to beginning
            if (audioBuffer.duration > 3 && speciesGroup.fullSpectrogramData) {
              const windowData = this.extractWindowData(
                speciesGroup,
                0,
                speciesGroup.windowTimeSteps
              );
              this.showSpectrogramWindow(speciesGroup, windowData, 0);
              speciesGroup.spectrogramLayout.xaxis.ticktext = [
                "0",
                "0.75",
                "1.5",
                "2.25",
                "3",
              ];
            }

            this.$forceUpdate();
          }
          this.stopCurrentAudio();
        };
      } catch (error) {
        console.error("Error playing audio:", error);
        const audio = new Audio(audioFile);
        audio.crossOrigin = "anonymous";
        audio.play();
      }
    },

    // Also update the playEnhanced method to support scrolling
    async playEnhanced(detection, speciesGroup) {
      if (!(await this.ensureAudio(detection))) {
        alert("Audio not available for this detection");
        return;
      }

      // Ensure we have the right species group
      if (!speciesGroup) {
        speciesGroup = this.groupedDetections.find((group) =>
          group.detections.some((d) => d.id === detection.id)
        );
      }

      // Update current detection
      if (speciesGroup) {
        speciesGroup.currentDetection = detection;
      }

      // Switch to spectrogram view immediately
      if (speciesGroup) {
        speciesGroup.window = "spectrogram";

        // Clear old spectrogram and force regeneration
        console.log("Enhanced: clearing old spectrogram");
        speciesGroup.spectrogram = null;
        speciesGroup.fullSpectrogramData = null;
        this.$forceUpdate();

        // Generate spectrogram if not already generated
        await this.generateSpectrogram(speciesGroup, detection.audioUrl);
      }

      // Show loading state
      if (speciesGroup) {
        this.$set(speciesGroup, "processingEnhanced", true);
        this.$forceUpdate();
      }

      this.initAudioContext();
      if (globalAudioContext.state === "suspended") {
        globalAudioContext.resume();
      }

      // Check if we have cached enhanced audio
      const cacheKey = `${detection.id}_${this.clipSeconds}_${
        speciesGroup ? speciesGroup.spCode : "default"
      }`;
      if (this.lastEnhancedId === cacheKey && this.lastEnhancedAudio) {
        const sourceNode = globalAudioContext.createBufferSource();
        sourceNode.buffer = this.lastEnhancedAudio;
        sourceNode.connect(globalAudioContext.destination);
        sourceNode.start();

        // Clear processing state for cached playback
        if (speciesGroup) {
          speciesGroup.processingEnhanced = false;
          this.$forceUpdate();
        }

        // Add scrolling support for cached playback
        if (speciesGroup && speciesGroup.totalDuration > 3) {
          speciesGroup.isPlaying = true;
          speciesGroup.playbackProgress = 0;
          const duration = this.lastEnhancedAudio.duration;
          const startTime = globalAudioContext.currentTime;

          // Calculate the original offset used for enhancement
          const offset = detection.offset || 0;
          let playbackStartOffset = 0;
          if (offset > 0) {
            playbackStartOffset = offset - Math.floor(this.clipSeconds / 2);
            if (playbackStartOffset < 0) playbackStartOffset = 0;
          }

          // Clear processing state for cached playback
          speciesGroup.processingEnhanced = false;

          this.playbackInterval = setInterval(() => {
            // Add null check
            if (!globalAudioContext || !startTime) {
              clearInterval(this.playbackInterval);
              return;
            }
            const elapsed = globalAudioContext.currentTime - startTime;
            const progress = (elapsed / duration) * 100;

            if (progress >= 100) {
              clearInterval(this.playbackInterval);
              speciesGroup.isPlaying = false;
              speciesGroup.playbackProgress = 0;

              // Reset spectrogram
              if (speciesGroup.fullSpectrogramData) {
                const windowData = this.extractWindowData(
                  speciesGroup,
                  0,
                  speciesGroup.windowTimeSteps
                );
                this.showSpectrogramWindow(speciesGroup, windowData, 0);
                speciesGroup.spectrogramLayout.xaxis.ticktext = [
                  "0",
                  "0.75",
                  "1.5",
                  "2.25",
                  "3",
                ];
              }
            } else {
              this.updateSpectrogramWindowDuringPlayback(
                speciesGroup,
                elapsed,
                playbackStartOffset
              );
            }
            this.$forceUpdate();
          }, 50);
        }

        console.log("Playing cached enhanced audio");
        return;
      }

      // The try block below declares its own cacheKey, which would shadow this one.
      const enhancedCacheKey = cacheKey;
      try {
        const response = await fetch(detection.audioUrl);
        const arrayBuffer = await response.arrayBuffer();

        // Bat recordings: skip enhancement and pitch shift, as Haikubox.vue does.
        let detectedSampleRate = await this.detectFlacSampleRate(arrayBuffer);
        if (!detectedSampleRate) {
          detectedSampleRate = await this.getAudioSampleRate(
            detection.audioUrl,
            arrayBuffer
          );
        }
        if (detectedSampleRate >= 176400) {
          await this.playBatPitchShift(
            arrayBuffer,
            detectedSampleRate,
            speciesGroup,
            enhancedCacheKey
          );
          return;
        }

        const audioBuffer = await globalAudioContext.decodeAudioData(
          arrayBuffer
        );

        let signal = audioBuffer.getChannelData(0);
        let playbackStartOffset = 0;

        // Handle offset for long files
        if (audioBuffer.duration > 3) {
          const offset = detection.offset || 0;

          if (offset > 0) {
            const startTime = offset - Math.floor(this.clipSeconds / 2);
            playbackStartOffset = startTime;
            console.log(
              `Enhancing ${
                this.clipSeconds
              }s clip: detection at ${offset}s, processing from ${startTime.toFixed(
                1
              )}s`
            );

            let startSample = Math.floor(startTime * audioBuffer.sampleRate);
            let endSample = Math.floor(
              (startTime + this.clipSeconds) * audioBuffer.sampleRate
            );

            if (startSample < 0) {
              startSample = 0;
              playbackStartOffset = 0;
            }
            if (endSample > signal.length) {
              endSample = signal.length;
              startSample = Math.max(
                0,
                endSample - this.clipSeconds * audioBuffer.sampleRate
              );
              playbackStartOffset = startSample / audioBuffer.sampleRate;
            }

            signal = signal.slice(startSample, endSample);
          } else {
            const startTime = audioBuffer.duration / 2 - this.clipSeconds / 2;
            playbackStartOffset = startTime;
            console.log(
              `No offset found, centering ${this.clipSeconds}s enhanced clip at middle`
            );

            let startSample = Math.floor(startTime * audioBuffer.sampleRate);
            let endSample = Math.floor(
              (startTime + this.clipSeconds) * audioBuffer.sampleRate
            );

            if (startSample < 0) {
              startSample = 0;
              playbackStartOffset = 0;
            }
            if (endSample > signal.length) {
              endSample = signal.length;
              startSample = Math.max(
                0,
                endSample - this.clipSeconds * audioBuffer.sampleRate
              );
              playbackStartOffset = startSample / audioBuffer.sampleRate;
            }

            signal = signal.slice(startSample, endSample);
          }
        }

        // Time-domain click removal for dolphins - SKIP STFT if only doing time domain
        if (speciesGroup && speciesGroup.spCode === "tursiopstrunc") {
          console.log("Starting optimized click removal for dolphin audio");
          const startProcessingTime = performance.now();

          // Optimized click removal parameters
          const clickWindowSize = 128; // samples to check around potential clicks
          const threshold = 2.0; // threshold multiplier - INCREASE THIS TO PRESERVE QUIETER SIGNALS (was 3.0)
          const medianWindowSize = 512; // smaller window for faster processing (was 512)

          // Fast median calculation using running median approach
          const clickLocations = [];
          const absSignal = signal.map((s) => Math.abs(s));

          // Simplified click detection - check every nth sample for speed
          const stride = 16;
          for (
            let i = medianWindowSize;
            i < signal.length - medianWindowSize;
            i += stride
          ) {
            // Get local window
            const localWindow = absSignal.slice(
              i - medianWindowSize / 2,
              i + medianWindowSize / 2
            );

            // Quick approximation of median using average of 25th and 75th percentiles
            localWindow.sort((a, b) => a - b);
            const q1 = localWindow[Math.floor(localWindow.length * 0.25)];
            const q3 = localWindow[Math.floor(localWindow.length * 0.75)];
            const approxMedian = (q1 + q3) / 2;

            // Simple threshold check
            const currentAbs = absSignal[i];
            if (currentAbs > approxMedian * threshold) {
              // Check neighboring samples to confirm it's a click
              for (
                let j = Math.max(0, i - 5);
                j <= Math.min(signal.length - 1, i + 5);
                j++
              ) {
                if (absSignal[j] > approxMedian * threshold) {
                  clickLocations.push(j);
                }
              }
            }
          }

          // Remove duplicates and sort
          const uniqueClicks = [...new Set(clickLocations)].sort(
            (a, b) => a - b
          );

          // Group nearby clicks
          const clickGroups = [];
          let currentGroup = [];

          for (let i = 0; i < uniqueClicks.length; i++) {
            if (currentGroup.length === 0) {
              currentGroup.push(uniqueClicks[i]);
            } else if (
              uniqueClicks[i] - currentGroup[currentGroup.length - 1] <
              clickWindowSize
            ) {
              currentGroup.push(uniqueClicks[i]);
            } else {
              if (currentGroup.length > 0) {
                clickGroups.push(currentGroup);
              }
              currentGroup = [uniqueClicks[i]];
            }
          }
          if (currentGroup.length > 0) {
            clickGroups.push(currentGroup);
          }

          // Remove clicks by interpolation
          for (const group of clickGroups) {
            const startIdx = Math.max(0, group[0] - 16);
            const endIdx = Math.min(
              signal.length - 1,
              group[group.length - 1] + 16
            );

            // Linear interpolation
            const startVal = signal[startIdx];
            const endVal = signal[endIdx];
            const span = endIdx - startIdx;

            for (let i = startIdx + 1; i < endIdx; i++) {
              const fraction = (i - startIdx) / span;
              signal[i] = startVal + (endVal - startVal) * fraction;
            }
          }

          const processingTime = performance.now() - startProcessingTime;
          console.log(
            `Removed ${
              clickGroups.length
            } click groups in ${processingTime.toFixed(0)}ms`
          );

          // For dolphins, create the enhanced buffer directly without STFT/IFFT
          const enhancedBuffer = globalAudioContext.createBuffer(
            1,
            signal.length,
            audioBuffer.sampleRate
          );
          const channelData = enhancedBuffer.getChannelData(0);

          // Better normalization for click-removed audio
          // Use percentile-based normalization to avoid being affected by remaining peaks
          const absSortedSignal = signal
            .map((s) => Math.abs(s))
            .sort((a, b) => a - b);
          const percentile95 =
            absSortedSignal[Math.floor(absSortedSignal.length * 0.95)];

          // Use 95th percentile for normalization, but clamp at 99th percentile
          const targetLevel = 0.7; // Target level for 95th percentile
          const normFactor = percentile95 > 0 ? targetLevel / percentile95 : 1;
          const maxGain = 100.0; // Maximum gain to apply
          const appliedGain = Math.min(normFactor, maxGain);

          console.log(
            `Normalization: 95th percentile=${percentile95.toFixed(
              4
            )}, gain=${appliedGain.toFixed(2)}`
          );

          // Apply normalization with soft clipping
          for (let i = 0; i < signal.length; i++) {
            let sample = signal[i] * appliedGain;

            // Soft clipping to prevent harsh distortion
            if (Math.abs(sample) > 0.95) {
              sample =
                Math.sign(sample) *
                (0.95 + 0.05 * Math.tanh((Math.abs(sample) - 0.95) * 2));
            }

            channelData[i] = sample;
          }

          // For dolphins, regenerate spectrogram using the click-removed audio
          if (speciesGroup) {
            console.log("Regenerating spectrogram with click-removed audio");

            // Clear the existing spectrogram first to prevent resize errors
            speciesGroup.spectrogram = null;
            speciesGroup.fullSpectrogramData = null;
            speciesGroup.loadingSpectrogram = true;
            speciesGroup.isEnhancedSpectrogram = true;
            this.$forceUpdate();

            // Wait for DOM to update and Plotly to clean up
            await this.$nextTick();
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Now generate the new spectrogram
            await this.generateSpectrogramForWindow(
              speciesGroup,
              enhancedBuffer
            );
            speciesGroup.loadingSpectrogram = false;
            this.$forceUpdate();
          }

          this.applyCos2Ramp(
            enhancedBuffer.getChannelData(0),
            enhancedBuffer.sampleRate,
            5
          );

          // Play the enhanced audio
          const sourceNode = globalAudioContext.createBufferSource();
          sourceNode.buffer = enhancedBuffer;
          sourceNode.connect(globalAudioContext.destination);
          sourceNode.start();

          // Cache the enhanced audio
          const cacheKey = `${detection.id}_${this.clipSeconds}_${
            speciesGroup ? speciesGroup.spCode : "default"
          }`;
          this.lastEnhancedId = cacheKey;
          this.lastEnhancedAudio = enhancedBuffer;
          this.lastEnhancedClipSeconds = this.clipSeconds;

          // Clear processing state
          if (speciesGroup) {
            speciesGroup.processingEnhanced = false;
            this.$forceUpdate();
          }

          // Handle playback tracking for dolphins
          if (speciesGroup) {
            speciesGroup.isPlaying = true;
            speciesGroup.playbackProgress = 0;

            const duration = enhancedBuffer.duration;
            const startTime = globalAudioContext.currentTime;

            // Set initial window position for spectrogram
            if (speciesGroup.totalDuration > 3) {
              this.updateSpectrogramWindowDuringPlayback(
                speciesGroup,
                0,
                playbackStartOffset
              );
            }

            this.playbackInterval = setInterval(() => {
              const elapsed = globalAudioContext.currentTime - startTime;
              const progress = (elapsed / duration) * 100;

              if (progress >= 100) {
                clearInterval(this.playbackInterval);
                speciesGroup.isPlaying = false;
                speciesGroup.playbackProgress = 0;

                // Reset spectrogram
                if (
                  speciesGroup.totalDuration > 3 &&
                  speciesGroup.fullSpectrogramData
                ) {
                  const windowData = this.extractWindowData(
                    speciesGroup,
                    0,
                    speciesGroup.windowTimeSteps
                  );
                  this.showSpectrogramWindow(speciesGroup, windowData, 0);
                  speciesGroup.spectrogramLayout.xaxis.ticktext = [
                    "0",
                    "0.75",
                    "1.5",
                    "2.25",
                    "3",
                  ];
                }
              } else {
                if (speciesGroup.totalDuration > 3) {
                  this.updateSpectrogramWindowDuringPlayback(
                    speciesGroup,
                    elapsed,
                    playbackStartOffset
                  );
                } else {
                  speciesGroup.playbackProgress = progress;
                }
              }
              this.$forceUpdate();
            }, 50);
          }

          sourceNode.onended = () => {
            if (speciesGroup) {
              speciesGroup.isPlaying = false;
              speciesGroup.playbackProgress = 0;

              // Reset spectrogram with proper checks
              if (
                speciesGroup.totalDuration > 3 &&
                speciesGroup.fullSpectrogramData &&
                speciesGroup.spectrogram &&
                speciesGroup.spectrogram[0]
              ) {
                try {
                  const windowData = this.extractWindowData(
                    speciesGroup,
                    0,
                    speciesGroup.windowTimeSteps
                  );
                  // Create new spectrogram object instead of modifying in place
                  const newSpectrogram = [
                    {
                      ...speciesGroup.spectrogram[0],
                      z: windowData,
                    },
                  ];
                  speciesGroup.spectrogram = newSpectrogram;

                  if (
                    speciesGroup.spectrogramLayout &&
                    speciesGroup.spectrogramLayout.xaxis
                  ) {
                    speciesGroup.currentWindowStart = 0;
                    speciesGroup.spectrogramLayout.xaxis.ticktext = [
                      "0",
                      "0.75",
                      "1.5",
                      "2.25",
                      "3",
                    ];
                  }
                } catch (e) {
                  console.warn("Error resetting spectrogram view:", e);
                }
              }

              this.$forceUpdate();
            }
          };

          return; // Skip STFT processing for dolphins
        }

        // For non-dolphin species, continue with STFT enhancement
        const fftSize = 512;
        const overlap = 256;
        const hopSize = fftSize - overlap;
        const stft = [];
        var meanMag = new Array(fftSize).fill(0);
        var stdMag = new Array(fftSize).fill(0);
        var numStrides = 0;

        // Generate Hann window
        const hann = this.generateHannWindow(fftSize);

        // Compute STFT
        for (let i = 0; i < signal.length - fftSize; i += hopSize) {
          const frame = signal.slice(i, i + fftSize);
          for (var k = 0; k < fftSize; k++) {
            frame[k] = frame[k] * hann[k];
          }
          var phasors = fft(frame);
          for (var j = 0; j < phasors.length; j++) {
            meanMag[j] += Math.sqrt(
              phasors[j][0] * phasors[j][0] + phasors[j][1] * phasors[j][1]
            );
          }
          numStrides++;
          stft.push([...phasors]);
        }

        // For non-dolphin species, apply frequency-based noise gating
        // Calculate mean
        for (let j = 0; j < fftSize; j++) {
          meanMag[j] = meanMag[j] / numStrides;
        }

        // Calculate SD
        for (let i = 0; i < stft.length; i++) {
          for (let j = 0; j < fftSize; j++) {
            let magnitude =
              Math.sqrt(
                stft[i][j][0] * stft[i][j][0] + stft[i][j][1] * stft[i][j][1]
              ) - meanMag[j];
            stdMag[j] += magnitude * magnitude;
          }
        }
        for (let j = 0; j < fftSize; j++) {
          stdMag[j] = Math.sqrt(stdMag[j] / numStrides);
        }

        // Gate spectrogram
        for (let i = 0; i < stft.length - 4; i++) {
          for (let j = 0; j < fftSize; j++) {
            let magnitude =
              (Math.sqrt(
                stft[i][j][0] * stft[i][j][0] + stft[i][j][1] * stft[i][j][1]
              ) +
                Math.sqrt(
                  stft[i + 1][j][0] * stft[i + 1][j][0] +
                    stft[i + 1][j][1] * stft[i + 1][j][1]
                ) +
                Math.sqrt(
                  stft[i + 2][j][0] * stft[i + 2][j][0] +
                    stft[i + 2][j][1] * stft[i + 2][j][1]
                ) +
                Math.sqrt(
                  stft[i + 3][j][0] * stft[i + 3][j][0] +
                    stft[i + 3][j][1] * stft[i + 3][j][1]
                ) +
                Math.sqrt(
                  stft[i + 4][j][0] * stft[i + 4][j][0] +
                    stft[i + 4][j][1] * stft[i + 4][j][1]
                )) /
              5;
            if (magnitude < meanMag[j] + 1.9 * stdMag[j] || j < 4) {
              stft[i][j][0] = stft[i][j][0] * 0.2;
              stft[i][j][1] = stft[i][j][1] * 0.2;
              stft[i][fftSize - 1 - j][0] = stft[i][fftSize - 1 - j][0] * 0.2;
              stft[i][fftSize - 1 - j][1] = stft[i][fftSize - 1 - j][1] * 0.2;
            }
          }
        }

        // Compute ISTFT
        let recoveredSignal = new Array(signal.length).fill(0);
        let recoveredSignalComplex = new Array(fftSize);
        for (let i = 0; i < stft.length - 4; i++) {
          recoveredSignalComplex = ifft(stft[i]);
          for (let j = 0; j < fftSize; j++) {
            if (i * hopSize + j < signal.length) {
              recoveredSignal[i * hopSize + j] += recoveredSignalComplex[j][0];
            }
          }
        }

        // Normalize
        var maxSignal = 0;
        for (let i = 0; i < recoveredSignal.length - 1000; i += 1000) {
          var tempMax = Math.max(...recoveredSignal.slice(i, i + 1000));
          if (tempMax > maxSignal) {
            maxSignal = tempMax;
          }
        }
        maxSignal = 0.9 / maxSignal;

        // Create enhanced audio buffer
        const enhancedBuffer = globalAudioContext.createBuffer(
          1,
          recoveredSignal.length,
          audioBuffer.sampleRate
        );
        const channelData = enhancedBuffer.getChannelData(0);
        for (let i = 0; i < recoveredSignal.length; i++) {
          channelData[i] = recoveredSignal[i] * maxSignal;
        }
        this.applyCos2Ramp(channelData, enhancedBuffer.sampleRate, 5);

        // Play enhanced audio
        const sourceNode = globalAudioContext.createBufferSource();
        sourceNode.buffer = enhancedBuffer;
        sourceNode.connect(globalAudioContext.destination);
        sourceNode.start();

        // Cache the enhanced audio
        const cacheKey = `${detection.id}_${this.clipSeconds}_${
          speciesGroup ? speciesGroup.spCode : "default"
        }`;
        this.lastEnhancedId = cacheKey;
        this.lastEnhancedAudio = enhancedBuffer;
        this.lastEnhancedClipSeconds = this.clipSeconds;

        // Clear processing state
        if (speciesGroup) {
          speciesGroup.processingEnhanced = false;
          this.$forceUpdate();
        }

        // Update playing state with scrolling support
        if (speciesGroup) {
          speciesGroup.isPlaying = true;
          speciesGroup.playbackProgress = 0;

          const duration = enhancedBuffer.duration;
          const startTime = globalAudioContext.currentTime;

          // Set initial window position for spectrogram
          if (speciesGroup.totalDuration > 3) {
            this.updateSpectrogramWindowDuringPlayback(
              speciesGroup,
              0,
              playbackStartOffset
            );
          }

          this.playbackInterval = setInterval(() => {
            const elapsed = globalAudioContext.currentTime - startTime;
            const progress = (elapsed / duration) * 100;

            if (progress >= 100) {
              clearInterval(this.playbackInterval);
              speciesGroup.isPlaying = false;
              speciesGroup.playbackProgress = 0;

              // Reset spectrogram
              if (
                speciesGroup.totalDuration > 3 &&
                speciesGroup.fullSpectrogramData
              ) {
                const windowData = this.extractWindowData(
                  speciesGroup,
                  0,
                  speciesGroup.windowTimeSteps
                );
                this.showSpectrogramWindow(speciesGroup, windowData, 0);
                speciesGroup.spectrogramLayout.xaxis.ticktext = [
                  "0",
                  "0.75",
                  "1.5",
                  "2.25",
                  "3",
                ];
              }
            } else {
              if (speciesGroup.totalDuration > 3) {
                this.updateSpectrogramWindowDuringPlayback(
                  speciesGroup,
                  elapsed,
                  playbackStartOffset
                );
              } else {
                speciesGroup.playbackProgress = progress;
              }
            }
            this.$forceUpdate();
          }, 50);
        }

        sourceNode.onended = () => {
          if (speciesGroup) {
            speciesGroup.isPlaying = false;
            speciesGroup.playbackProgress = 0;

            // Reset spectrogram
            if (
              speciesGroup.totalDuration > 3 &&
              speciesGroup.fullSpectrogramData
            ) {
              const windowData = this.extractWindowData(
                speciesGroup,
                0,
                speciesGroup.windowTimeSteps
              );
              this.showSpectrogramWindow(speciesGroup, windowData, 0);
              speciesGroup.spectrogramLayout.xaxis.ticktext = [
                "0",
                "0.75",
                "1.5",
                "2.25",
                "3",
              ];
            }

            this.$forceUpdate();
          }
        };
      } catch (error) {
        console.error("Error playing enhanced audio:", error);
        // Clear processing state
        if (speciesGroup) {
          speciesGroup.processingEnhanced = false;
          this.$forceUpdate();
        }
        alert("Error enhancing audio. Playing normal version instead.");
        this.playSound(detection);
      }
    },

    // Haikubox.vue's bat path: decode at the native rate (so the ultrasound
    // survives), play the whole file at a tenth of the rate, no clip window.
    async playBatPitchShift(arrayBuffer, sampleRate, speciesGroup, cacheKey) {
      const audioContext = await this.getMatchingAudioContext(sampleRate);
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }
      const sample = await this.decodeAudioData(
        audioContext,
        arrayBuffer.slice(0)
      );
      const signal = sample.getChannelData(0);
      const outputSampleRate = Math.round(sample.sampleRate / 10);

      // Normalize
      const peak = signal.reduce((acc, val) => Math.max(acc, Math.abs(val)), 0);
      const gain = peak > 0 ? 0.8 / peak : 1;

      // Create buffer at lower sample rate for pitch shift
      const audioBuffer = audioContext.createBuffer(
        1,
        signal.length,
        outputSampleRate
      );
      const channelData = audioBuffer.getChannelData(0);
      for (let i = 0; i < signal.length; i++) {
        channelData[i] = signal[i] * gain;
      }
      this.applyCos2Ramp(channelData, outputSampleRate, 5);

      const sourceNode = audioContext.createBufferSource();
      sourceNode.buffer = audioBuffer;
      sourceNode.connect(audioContext.destination);

      this.stopCurrentAudio();
      this.currentAudioSource = sourceNode;
      if (speciesGroup) {
        speciesGroup.processingEnhanced = false;
        speciesGroup.isPlaying = true;
        speciesGroup.playbackProgress = 0;
      }

      const playbackStartTime = audioContext.currentTime;
      const playbackDuration = audioBuffer.duration;
      this.playbackInterval = setInterval(() => {
        const elapsed = audioContext.currentTime - playbackStartTime;
        const progress = Math.min(
          ((elapsed + 0.04) / playbackDuration) * 100,
          100
        );
        if (speciesGroup) {
          speciesGroup.playbackProgress = progress >= 100 ? 0 : progress;
        }
        if (progress >= 100) {
          clearInterval(this.playbackInterval);
          this.playbackInterval = null;
        }
        this.$forceUpdate();
      }, 25);

      sourceNode.onended = () => {
        if (speciesGroup) {
          speciesGroup.isPlaying = false;
          speciesGroup.playbackProgress = 0;
        }
        if (this.playbackInterval) {
          clearInterval(this.playbackInterval);
          this.playbackInterval = null;
        }
        if (this.currentAudioSource === sourceNode) {
          this.currentAudioSource = null;
        }
        this.$forceUpdate();
      };

      sourceNode.start(0);
      // Replays reuse the pitch-shifted buffer.
      this.lastEnhancedId = cacheKey;
      this.lastEnhancedAudio = audioBuffer;
    },

    applyCos2Ramp(signal, sampleRate, rampDurationMs = 5) {
      // Calculate ramp length in samples (default 5ms)
      const rampLength = Math.floor((rampDurationMs / 1000) * sampleRate);
      const effectiveRampLength = Math.min(
        rampLength,
        Math.floor(signal.length / 2)
      );

      // Apply fade-in (cos² ramp at the beginning)
      for (let i = 0; i < effectiveRampLength; i++) {
        const angle = (Math.PI / 2) * (i / effectiveRampLength);
        const ramp = Math.sin(angle) * Math.sin(angle); // sin²(x) = cos²(π/2 - x)
        signal[i] *= ramp;
      }

      // Apply fade-out (cos² ramp at the end)
      for (let i = 0; i < effectiveRampLength; i++) {
        const idx = signal.length - 1 - i;
        const angle = (Math.PI / 2) * (i / effectiveRampLength);
        const ramp = Math.sin(angle) * Math.sin(angle);
        signal[idx] *= ramp;
      }

      return signal;
    },

    // Add these other supporting methods you'll need:

    async playSound(detection) {
      if (!(await this.ensureAudio(detection))) {
        alert("Audio not available for this detection");
        return;
      }

      // Stop any currently playing audio first
      this.stopCurrentAudio();

      try {
        // Find the species group for this detection
        const speciesGroup = this.groupedDetections.find((group) =>
          group.detections.some((d) => d.id === detection.id)
        );

        if (speciesGroup) {
          // Check if this is a different audio file than what's currently loaded
          const previousUrl = speciesGroup.currentDetection
            ? speciesGroup.currentDetection.audioUrl
            : null;
          const isNewAudio = detection.audioUrl !== previousUrl;

          // Set current detection
          speciesGroup.currentDetection = detection;

          // Always clear spectrogram data when switching detections
          if (isNewAudio || !speciesGroup.spectrogram) {
            console.log(
              "Clearing old spectrogram data for new audio:",
              detection.audioUrl
            );
            speciesGroup.spectrogram = null;
            speciesGroup.fullSpectrogramData = null;
            speciesGroup.totalTimeSteps = null;
            speciesGroup.totalDuration = null;
            speciesGroup.windowTimeSteps = null;
            speciesGroup.currentWindowStart = null;
            speciesGroup.loadingSpectrogram = true;

            // Remove from cache to force regeneration
            if (this.audioCache && previousUrl && isNewAudio) {
              this.audioCache.delete(previousUrl);
            }

            // Force immediate UI update to show loading state
            this.$forceUpdate();
            await this.$nextTick();
          }

          // Switch to spectrogram view
          speciesGroup.window = "spectrogram";

          // Always regenerate spectrogram for the current detection
          console.log("Generating spectrogram for", detection.audioUrl);
          await this.generateSpectrogram(speciesGroup, detection.audioUrl);

          // Force update after spectrogram generation
          this.$forceUpdate();
          await this.$nextTick();
        }

        // Use the enhanced playFile method - only after spectrogram is ready
        await this.playFile(detection.audioUrl, detection);
      } catch (error) {
        console.error("Error playing audio:", error);
        alert("Error playing audio. The file may not be accessible.");
      }
    },

    async handleDetectionChange(speciesGroup, detection) {
      // Immediately update current detection
      speciesGroup.currentDetection = detection;

      // Clear spectrogram to show loading state
      console.log("Detection changed, clearing spectrogram");
      speciesGroup.spectrogram = null;
      speciesGroup.fullSpectrogramData = null;
      speciesGroup.loadingSpectrogram = true;
      this.$forceUpdate();

      // Small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Play the audio which will regenerate spectrogram
      if (detection.hasAudio) {
        await this.playSound(detection);
      }
    },

    stopCurrentAudio() {
      if (this.currentAudioSource) {
        try {
          this.currentAudioSource.stop();
        } catch (e) {
          // Already stopped
        }
        this.currentAudioSource = null;
      }

      if (this.playbackInterval) {
        clearInterval(this.playbackInterval);
        this.playbackInterval = null;
      }

      // Reset all playing states
      this.groupedDetections.forEach((group) => {
        group.isPlaying = false;
        group.playbackProgress = 0;
        group.processingEnhanced = false;
      });
    },

    generateHannWindow(length) {
      const window = new Float32Array(length);
      for (let i = 0; i < length; i++) {
        window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (length - 1)));
      }
      return window;
    },

    getOptimalFFTLength(sampleRate) {
      const fftLengthMap = {
        8000: 256, // 32ms window, 31.25 Hz/bin
        16000: 512, // 32ms window, 31.25 Hz/bin
        22050: 512, // 23.2ms window, 43.07 Hz/bin
        24000: 512, // 21.3ms window, 46.88 Hz/bin
        32000: 1024, // 32ms window, 31.25 Hz/bin
        44100: 1024, // 23.2ms window, 43.07 Hz/bin
        48000: 1024, // 21.3ms window, 46.88 Hz/bin
      };

      // Find closest sample rate in map
      const rates = Object.keys(fftLengthMap)
        .map(Number)
        .sort((a, b) => a - b);
      let closestRate = rates[0];

      for (const rate of rates) {
        if (Math.abs(sampleRate - rate) < Math.abs(sampleRate - closestRate)) {
          closestRate = rate;
        }
      }

      return fftLengthMap[closestRate] || 512; // Default to 512
    },

    async setWindowSpecies(speciesGroup, window) {
      speciesGroup.window = window;

      if (window === "spectrogram" && !speciesGroup.spectrogram) {
        // Use current detection's audio or fall back to first one
        const detection =
          speciesGroup.currentDetection || speciesGroup.detections[0];
        if (detection && (await this.ensureAudio(detection))) {
          await this.generateSpectrogram(speciesGroup, detection.audioUrl);
        }
      }

      // Force re-render
      this.$forceUpdate();
    },

    async updateSpectrogramWindow(speciesGroup) {
      if (!speciesGroup.spectrogram) return;

      const audioUrl = speciesGroup.currentDetection.audioUrl;
      if (!audioUrl || !this.audioCache.has(audioUrl)) return;

      // Get cached audio buffer
      const cachedData = this.audioCache.get(audioUrl);
      if (!cachedData || !cachedData.audioBuffer) return;

      const audioBuffer = cachedData.audioBuffer;

      // Regenerate spectrogram for new window
      await this.generateSpectrogramForWindow(speciesGroup, audioBuffer);
      this.$forceUpdate();
    },

    // Helper method to decode audio data with promise wrapper
    decodeAudioData(audioContext, arrayBuffer) {
      return new Promise((resolve, reject) => {
        audioContext.decodeAudioData(
          arrayBuffer,
          (buffer) => resolve(buffer),
          (error) => reject(error)
        );
      });
    },

    // Clear cache when component is destroyed or when needed
    clearAudioCache() {
      this.audioCache.clear();
    },

    toggleDropdown(spCode) {
      this.expandedSpecies = this.expandedSpecies === spCode ? null : spCode;
    },

    onDialogChange(value) {
      if (!value) {
        // Dialog is closing, reset form after a delay
        setTimeout(() => {
          this.resetForm();
        }, 300);
      }
    },

    handleOutsideClick(event) {
      // Close dropdown if clicking outside
      if (
        !event.target.closest(".detection-dropdown") &&
        !event.target.closest(".v-btn")
      ) {
        this.expandedSpecies = null;
      }
    },
  },
};
</script>

<!-- 
IMPORTANT: This component requires dsp.js and fft-js for audio processing.
Install them with: 
npm install dsp.js
npm install fft-js
-->

<style scoped>
#group-dashboard {
  font-family: Quicksand, Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f5f5f5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Make the container flex and full height */
.dashboard-container {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px); /* Adjust based on your header height */
  padding-bottom: 0 !important;
}

/* Main content should not grow */
.main-content {
  flex: 0 0 auto;
}

/* Spacer to push partners down */
.flex-spacer {
  flex: 1 1 auto;
  min-height: 20px; /* Minimum spacing */
}

/* Partners card stays at bottom */
.partners-card {
  flex: 0 0 auto;
  margin-bottom: 0 !important;
}

/* Map card + basemap toggle button */
.map-card {
  position: relative;
}

.map-style-toggle {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000; /* above Leaflet panes/controls */
  background-color: white !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

/* Partner logos — uniform height and spacing */
.partner-logo-box {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-logo {
  height: 100px;
  width: auto;
  max-width: 90vw; /* safety on small screens for very wide banner logos */
  object-fit: contain;
}

/* Detection dropdown overlay */
.detection-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-top: 2px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.detection-list {
  overflow-y: auto;
  max-height: 250px;
}

/* Ensure parent cards have relative positioning */
.v-card {
  position: relative;
}

/* Species cards need visible overflow for dropdown */
.species-card {
  overflow: visible !important;
}

/* Avatar styling */
.v-avatar {
  margin: 20px auto;
}

/* Expanded view for detections */
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.v-expansion-panel--active .v-expansion-panel-content {
  max-height: 600px;
  overflow-y: auto;
}

/* Spectrogram progress bar */
.spectrogram-progress-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #dae6a2;
  box-shadow: 0 0 4px rgba(255, 0, 0, 0.5);
  pointer-events: none;
  z-index: 10;
}
</style>

<style>
/* Import Leaflet marker cluster styles */
.marker-cluster-small {
  background-color: rgba(76, 175, 80, 0.6);
}
.marker-cluster-small div {
  background-color: rgba(76, 175, 80, 0.6);
}
.marker-cluster-medium {
  background-color: rgba(76, 175, 80, 0.6);
}
.marker-cluster-medium div {
  background-color: rgba(69, 160, 73, 0.6);
}
.marker-cluster-large {
  background-color: rgba(76, 175, 80, 0.6);
}
.marker-cluster-large div {
  background-color: rgba(56, 142, 60, 0.6);
}

/* Custom marker icon styles */
.custom-div-icon {
  background: transparent;
  border: none;
}
</style>